import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { formatINR, formatDate, formatDateTime, waLink } from "../lib/format";
import {
    AlertCircle, Phone, MessageCircle, FileDown, FileText, ShieldCheck,
    Wallet, Coins, ScrollText, LayoutDashboard, User, ClipboardList,
    TrendingUp, ReceiptText, FolderClosed, Bell, LifeBuoy, Calculator,
    Menu, X, LogOut, ChevronRight, Sparkles, Info, ArrowDownCircle,
    CheckCircle2, Clock, XCircle,
} from "lucide-react";
import { EntryDetailDialog } from "../components/EntryDetailDialog";
import { BrandMark } from "../components/BrandMark";
import { OFFICE_HOURS_LINES } from "../lib/office-hours";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HIDDEN_STATUSES = new Set(["pending", "rejected"]);

/* ---------- next installment due helper (client-side, display only) ---------- */
function nextInstallment(ln) {
    if (!ln?.start_date) return { date: null, amount: 0 };
    const tenure = Number(ln.tenure_months || 0);
    const emi = Number(ln.emi || 0);
    const principal = Number(ln.principal || 0);
    const paidPrincipal = Number(ln.total_principal_paid || 0);
    let cyclesPaid = 0;
    if (principal > 0 && tenure > 0) {
        cyclesPaid = Math.min(tenure - 1, Math.max(0, Math.round(paidPrincipal / (principal / tenure))));
    }
    const start = new Date(ln.start_date + "T00:00:00Z");
    const nextCycle = Math.min(tenure, cyclesPaid + 1);
    const d = new Date(start);
    d.setUTCMonth(d.getUTCMonth() + nextCycle);
    return { date: d.toISOString().slice(0, 10), amount: emi };
}

/* ---------- due-status helper (per business spec: due_soon 5d before, past_due 1-10d, overdue >10d) ---------- */
function loanDueStatus(ln) {
    if (!ln || ln.status !== "active") return null;
    const outstanding = Number(ln.outstanding_principal || 0);
    if (outstanding <= 0) return null;
    const next = nextInstallment(ln);
    if (!next.date) return null;
    const due = new Date(next.date + "T00:00:00Z");
    const today = new Date();
    const utcToday = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
    const diffDays = Math.round((due - utcToday) / 86400000);
    if (diffDays > 5) return null;
    if (diffDays >= 0) return { kind: "due_soon", label: "Due Soon", days: diffDays, tone: "amber" };
    const pastDays = -diffDays;
    if (pastDays <= 10) return { kind: "past_due", label: "Past Due", days: pastDays, tone: "orange" };
    return { kind: "overdue", label: "Overdue", days: pastDays, tone: "rose" };
}

/* ---------- pending interest helper ---------- */
function pendingInterest(ln) {
    // Approximates the "Pending Interest" the portal already computes on the backend.
    // For read-only display: rate * outstanding * completed cycles - interest_paid (floor at 0).
    if (!ln || !ln.start_date || !ln.interest_rate) return 0;
    const rate = Number(ln.interest_rate || 0);
    const outstanding = Number(ln.outstanding_principal || 0);
    if (rate <= 0 || outstanding <= 0) return 0;
    const start = new Date(ln.start_date + "T00:00:00Z");
    const today = new Date();
    const months =
        (today.getUTCFullYear() - start.getUTCFullYear()) * 12 +
        (today.getUTCMonth() - start.getUTCMonth()) -
        (today.getUTCDate() < start.getUTCDate() ? 1 : 0);
    const completed = Math.max(0, months);
    const expected = completed * outstanding * (rate / 100);
    const paid = Number(ln.total_interest_paid || 0);
    return Math.max(0, Math.round((expected - paid) * 100) / 100);
}

/* ---------- helpers ---------- */
function PdfButton({ path, token, label, testid }) {
    function go() {
        const url = `${API_BASE}${path}${path.includes("?") ? "&" : "?"}t=${encodeURIComponent(token)}`;
        window.open(url, "_blank", "noopener,noreferrer");
    }
    return (
        <Button
            onClick={go}
            variant="outline"
            data-testid={testid}
            className="h-10 rounded-xl"
        >
            <FileDown className="h-4 w-4 mr-1.5" /> {label}
        </Button>
    );
}

function Stat({ label, value, accent }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                {label}
            </div>
            <div className={`num text-sm font-bold mt-0.5 ${accent ? "text-brand-royal text-base" : "text-slate-900"}`}>
                {value}
            </div>
        </div>
    );
}

function ComingSoon({ title, icon: Icon }) {
    return (
        <Card className="rounded-3xl border-slate-100 p-10 sm:p-14 text-center bg-gradient-to-br from-white to-brand-soft/30"
            data-testid="portal-coming-soon"
        >
            <div className="mx-auto h-16 w-16 rounded-2xl bg-brand-soft grid place-items-center">
                <Icon className="h-7 w-7 text-brand-royal" strokeWidth={1.6} />
            </div>
            <h3 className="mt-5 font-display font-bold text-brand-navy text-2xl">{title}</h3>
            <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">
                Coming in next step.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white border border-brand-soft px-4 py-1.5 text-xs font-semibold text-brand-royal">
                <Sparkles className="h-3.5 w-3.5" /> On the roadmap
            </div>
        </Card>
    );
}

/* ---------- navigation config ---------- */
const NAV = [
    { key: "dashboard",     label: "Dashboard",             icon: LayoutDashboard,  ready: true },
    { key: "profile",       label: "My Profile",            icon: User,             ready: true },
    { key: "loans",         label: "My Loans",              icon: Wallet,           ready: true },
    { key: "schedule",      label: "Repayment Schedule",    icon: ClipboardList,    ready: true },
    { key: "savings",       label: "Saving Scheme",         icon: Coins,            ready: true },
    { key: "investment",    label: "Investment",            icon: TrendingUp,       ready: true },
    { key: "statements",    label: "Statements",            icon: ScrollText,       ready: true },
    { key: "receipts",      label: "Receipts",              icon: ReceiptText,      ready: true },
    { key: "documents",     label: "Documents",             icon: FolderClosed,     ready: true },
    { key: "notifications", label: "Notifications",         icon: Bell,             ready: true },
    { key: "support",       label: "Complaints & Support",  icon: LifeBuoy,         ready: true },
    { key: "calculators",   label: "Calculators",           icon: Calculator,       ready: true },
    { key: "security",      label: "Security Settings",     icon: ShieldCheck,      ready: true },
];

/* ============================================================ */
export default function CustomerPortal() {
    const [params] = useSearchParams();
    const token = params.get("t") || "";
    const [me, setMe] = useState(null);
    const [payments, setPayments] = useState([]);
    const [settings, setSettings] = useState(null);
    const [error, setError] = useState("");

    const [active, setActive] = useState("dashboard");
    const [drawerOpen, setDrawerOpen] = useState(false);

    useEffect(() => {
        if (!token) {
            setError("Portal link is missing the security token.");
            return;
        }
        const client = axios.create({ baseURL: API_BASE });
        Promise.all([
            client.get("/portal/me", { params: { t: token } }),
            client.get("/portal/payments", { params: { t: token } }),
            client.get("/portal/settings", { params: { t: token } }),
        ])
            .then(([a, b, c]) => {
                setMe(a.data);
                setPayments(b.data);
                setSettings(c.data);
            })
            .catch((e) => {
                setError(e?.response?.data?.detail || "Could not open portal");
            });
        // Best-effort push subscribe. Never blocks the portal on failure.
        import("../lib/push").then(({ enableCustomerPush }) => {
            enableCustomerPush({ api: axios.create({ baseURL: API_BASE }), token }).catch(() => {});
        }).catch(() => {});
    }, [token]);

    /* filter out any pending/rejected records (defence in depth) */
    const loans = useMemo(() =>
        (me?.loans || []).filter((l) => !HIDDEN_STATUSES.has(String(l.status || "").toLowerCase())),
        [me]
    );
    const pigmy = useMemo(() =>
        (me?.pigmy_accounts || [])
            .filter((a) => !HIDDEN_STATUSES.has(String(a.status || "").toLowerCase()))
            .map((a) => ({
                ...a,
                // Customer-facing rename: internal DB code "PG####" is displayed as "SS####".
                account_code: (a.account_code || "").replace(/^PG/, "SS"),
            })),
        [me]
    );
    const summary = useMemo(() => {
        const activeLoans = loans.filter((l) => l.status === "active");
        return {
            active_loans: activeLoans.length,
            total_outstanding: activeLoans.reduce((s, l) => s + Number(l.outstanding_principal || 0), 0),
            savings_balance_total: pigmy.reduce((s, a) => s + Number(a.balance || 0), 0),
        };
    }, [loans, pigmy]);

    if (error) {
        return (
            <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
                <Card className="max-w-md p-8 rounded-2xl text-center">
                    <AlertCircle className="h-10 w-10 text-rose-600 mx-auto mb-2" />
                    <h1 className="font-display text-xl font-bold text-slate-900">
                        Link unavailable
                    </h1>
                    <p className="text-sm text-slate-500 mt-2">{error}</p>
                    <p className="text-xs text-slate-400 mt-3">
                        Please contact your branch or sign in again.
                    </p>
                    <Link
                        to="/customer-login"
                        className="mt-5 inline-block text-brand-royal font-semibold hover:underline text-sm"
                        data-testid="portal-back-to-login"
                    >
                        Back to Customer Login
                    </Link>
                </Card>
            </div>
        );
    }

    if (!me) {
        return (
            <div className="min-h-screen grid place-items-center bg-slate-50">
                <div className="text-slate-500">Loading your portal…</div>
            </div>
        );
    }

    const c = me.customer;
    const companyName = settings?.company_name || "Lokdhara Microfinance";
    const activeItem = NAV.find((n) => n.key === active) || NAV[0];

    const gotoSection = (key) => {
        setActive(key);
        setDrawerOpen(false);
        try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* noop */ }
    };

    return (
        <div className="min-h-screen bg-slate-50 lg:pl-72">
            {/* ============ SIDEBAR (desktop) ============ */}
            <aside className="hidden lg:flex fixed inset-y-0 left-0 w-72 bg-brand-navy text-slate-100 flex-col z-30">
                <div className="h-20 flex items-center px-5 border-b border-white/10">
                    <BrandMark size={40} variant="white" />
                </div>
                <div className="px-5 py-5 border-b border-white/10">
                    <div className="text-[10px] uppercase tracking-widest text-brand-tint/80 font-semibold">
                        Customer
                    </div>
                    <div className="mt-1 font-display font-bold text-white text-sm leading-tight truncate">
                        {c.name}
                    </div>
                    <div className="mt-1 text-xs text-slate-300 font-mono">
                        {c.customer_code}
                    </div>
                </div>
                <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1"
                    data-testid="portal-nav-desktop"
                >
                    {NAV.map((n) => {
                        const isActive = n.key === active;
                        const Icon = n.icon;
                        return (
                            <button
                                key={n.key}
                                onClick={() => gotoSection(n.key)}
                                className={`w-full flex items-center gap-3 px-3 h-10 rounded-xl text-sm font-medium transition ${
                                    isActive
                                        ? "bg-white text-brand-navy shadow-card"
                                        : "text-slate-200 hover:bg-white/5"
                                }`}
                                data-testid={`portal-nav-${n.key}`}
                            >
                                <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                                <span className="flex-1 text-left">{n.label}</span>
                                {!n.ready && (
                                    <span className="text-[9px] uppercase font-bold text-brand-tint bg-white/10 rounded px-1.5 py-0.5">
                                        Soon
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </nav>
                <div className="px-4 py-4 border-t border-white/10">
                    <Link
                        to="/customer-login"
                        className="flex items-center gap-2 text-xs text-slate-300 hover:text-white"
                        data-testid="portal-logout-desktop"
                    >
                        <LogOut className="h-3.5 w-3.5" />
                        Sign out
                    </Link>
                </div>
            </aside>

            {/* ============ MOBILE TOPBAR ============ */}
            <header className="lg:hidden sticky top-0 z-40 bg-brand-navy text-white">
                <div className="h-16 flex items-center justify-between px-4">
                    <button
                        aria-label="Open menu"
                        onClick={() => setDrawerOpen(true)}
                        className="h-10 w-10 rounded-lg bg-white/10 grid place-items-center"
                        data-testid="portal-mobile-menu-btn"
                    >
                        <Menu className="h-5 w-5" />
                    </button>
                    <BrandMark size={30} variant="white" />
                    <Badge
                        data-testid="portal-secure-badge"
                        className="bg-white/10 text-white border-transparent hover:bg-white/10"
                    >
                        <ShieldCheck className="h-3 w-3 mr-1" />
                        Secure
                    </Badge>
                </div>
            </header>

            {/* ============ MOBILE DRAWER ============ */}
            {drawerOpen && (
                <div className="lg:hidden fixed inset-0 bg-slate-900/50 z-50" onClick={() => setDrawerOpen(false)}>
                    <aside className="absolute left-0 top-0 h-full w-80 max-w-[85vw] bg-brand-navy text-slate-100 flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="portal-mobile-drawer"
                    >
                        <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
                            <BrandMark size={28} variant="white" />
                            <button
                                aria-label="Close menu"
                                onClick={() => setDrawerOpen(false)}
                                className="h-9 w-9 rounded-lg bg-white/10 grid place-items-center"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="px-4 py-4 border-b border-white/10">
                            <div className="text-[10px] uppercase tracking-widest text-brand-tint/80 font-semibold">
                                Customer
                            </div>
                            <div className="mt-1 font-display font-bold text-white text-sm truncate">
                                {c.name}
                            </div>
                            <div className="mt-1 text-xs text-slate-300 font-mono">
                                {c.customer_code}
                            </div>
                        </div>
                        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1"
                            data-testid="portal-nav-mobile"
                        >
                            {NAV.map((n) => {
                                const isActive = n.key === active;
                                const Icon = n.icon;
                                return (
                                    <button
                                        key={n.key}
                                        onClick={() => gotoSection(n.key)}
                                        className={`w-full flex items-center gap-3 px-3 h-11 rounded-xl text-sm font-medium transition ${
                                            isActive
                                                ? "bg-white text-brand-navy shadow-card"
                                                : "text-slate-200 hover:bg-white/5"
                                        }`}
                                        data-testid={`portal-navm-${n.key}`}
                                    >
                                        <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                                        <span className="flex-1 text-left">{n.label}</span>
                                        {!n.ready && (
                                            <span className="text-[9px] uppercase font-bold text-brand-tint bg-white/10 rounded px-1.5 py-0.5">
                                                Soon
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </nav>
                        <div className="px-4 py-4 border-t border-white/10">
                            <Link
                                to="/customer-login"
                                className="flex items-center gap-2 text-xs text-slate-300 hover:text-white"
                                data-testid="portal-logout-mobile"
                            >
                                <LogOut className="h-3.5 w-3.5" />
                                Sign out
                            </Link>
                        </div>
                    </aside>
                </div>
            )}

            {/* ============ MAIN ============ */}
            <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
                <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-brand-royal font-semibold">
                            {companyName} · Customer Portal
                        </div>
                        <h1 className="mt-1 font-display text-2xl sm:text-3xl font-bold text-brand-navy tracking-tight">
                            {activeItem.label}
                        </h1>
                    </div>
                    <Badge
                        className="bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50 hidden lg:inline-flex"
                        data-testid="portal-secure-badge-desktop"
                    >
                        <ShieldCheck className="h-3 w-3 mr-1" /> Secure
                    </Badge>
                </div>

                {/* ---------- DASHBOARD ---------- */}
                {active === "dashboard" && (
                    <div className="space-y-6" data-testid="portal-dashboard">
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                            <MetricCard
                                icon={Wallet}
                                label="Active Loans"
                                value={summary.active_loans}
                                accent="text-brand-navy"
                                testid="portal-metric-active-loans"
                            />
                            <MetricCard
                                icon={AlertCircle}
                                label="Loan Outstanding"
                                value={formatINR(summary.total_outstanding)}
                                accent="text-rose-600"
                                testid="portal-metric-outstanding"
                            />
                            <MetricCard
                                icon={Coins}
                                label="Saving Scheme Balance"
                                value={formatINR(summary.savings_balance_total)}
                                accent="text-emerald-700"
                                testid="portal-metric-savings"
                            />
                            <MetricCard
                                icon={TrendingUp}
                                label="Investment Summary"
                                value="—"
                                sub="Coming soon"
                                accent="text-slate-500"
                                testid="portal-metric-investment"
                            />
                        </div>

                        <Card className="rounded-2xl border-slate-100 p-5 sm:p-6"
                            data-testid="portal-dashboard-recent"
                        >
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <div className="text-[11px] uppercase tracking-widest text-brand-royal font-semibold">
                                        Recent Approved Payments
                                    </div>
                                    <h3 className="mt-1 font-display font-bold text-brand-navy text-lg">
                                        Last {Math.min(5, payments.length)} receipt{payments.length === 1 ? "" : "s"}
                                    </h3>
                                </div>
                                <button
                                    onClick={() => gotoSection("receipts")}
                                    className="text-xs font-semibold text-brand-royal hover:underline inline-flex items-center"
                                    data-testid="portal-view-all-receipts"
                                >
                                    View all <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                                </button>
                            </div>
                            {payments.length === 0 ? (
                                <div className="p-6 text-center text-sm text-slate-400">
                                    No approved payments yet.
                                </div>
                            ) : (
                                <div className="divide-y divide-slate-100">
                                    {payments.slice(0, 5).map((p) => (
                                        <PaymentRow key={p.id} p={p} />
                                    ))}
                                </div>
                            )}
                        </Card>

                        <Card className="rounded-2xl border-brand-soft bg-brand-soft/40 p-5 flex items-start gap-4"
                            data-testid="portal-note"
                        >
                            <div className="h-10 w-10 rounded-xl bg-white grid place-items-center shrink-0">
                                <Info className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                            </div>
                            <div className="text-sm text-slate-700 leading-relaxed">
                                This is a secure read-only portal. Only approved transactions are shown.
                                For account changes, please contact your branch.
                            </div>
                        </Card>
                    </div>
                )}

                {/* ---------- MY PROFILE ---------- */}
                {active === "profile" && (
                    <Card className="rounded-2xl border-slate-100 p-6" data-testid="portal-profile">
                        <div className="grid gap-4 sm:grid-cols-2">
                            <ProfileRow label="Customer ID" value={c.customer_code} mono />
                            <ProfileRow label="Full Name" value={c.name} />
                            <ProfileRow label="Mobile" value={c.mobile} />
                            <ProfileRow label="Address" value={c.address || "—"} full />
                        </div>
                        <p className="mt-6 text-xs text-slate-500 italic">
                            To update these details please contact your branch.
                        </p>
                    </Card>
                )}

                {/* ---------- MY LOANS ---------- */}
                {active === "loans" && (
                    <div className="space-y-4" data-testid="portal-loans-list">
                        {loans.length === 0 ? (
                            <Card className="rounded-2xl border-slate-100 p-10 text-center text-sm text-slate-400">
                                No approved loans yet.
                            </Card>
                        ) : loans.map((ln) => <LoanCard key={ln.id} ln={ln} token={token} />)}
                    </div>
                )}

                {/* ---------- REPAYMENT SCHEDULE ---------- */}
                {active === "schedule" && (
                    <RepaymentSchedule loans={loans} token={token} />
                )}

                {/* ---------- DAILY SAVINGS (renamed from Pigmy) ---------- */}
                {active === "savings" && (
                    <DailySavings accounts={pigmy} token={token} />
                )}

                {/* ---------- INVESTMENT ---------- */}
                {active === "investment" && <Investment token={token} />}

                {/* ---------- STATEMENTS ---------- */}
                {active === "statements" && (
                    <Statements loans={loans} pigmy={pigmy} token={token} />
                )}

                {/* ---------- RECEIPTS ---------- */}
                {active === "receipts" && (
                    <Receipts
                        payments={payments}
                        loans={loans}
                        pigmy={pigmy}
                        token={token}
                    />
                )}

                {/* ---------- DOCUMENTS ---------- */}
                {active === "documents" && (
                    <Documents loans={loans} pigmy={pigmy} token={token} />
                )}

                {/* ---------- NOTIFICATIONS ---------- */}
                {active === "notifications" && (
                    <Notifications
                        customer={c}
                        loans={loans}
                        pigmy={pigmy}
                        payments={payments}
                        token={token}
                    />
                )}

                {/* ---------- COMPLAINTS & SUPPORT ---------- */}
                {active === "support" && (
                    <Support
                        settings={settings}
                        companyName={companyName}
                        customer={c}
                        token={token}
                    />
                )}

                {/* ---------- CALCULATORS ---------- */}
                {active === "calculators" && <Calculators />}

                {/* ---------- SECURITY SETTINGS ---------- */}
                {active === "security" && (
                    <SecuritySettings
                        customer={c}
                        token={token}
                        onUpdated={() => {
                            /* refresh me so timestamps update */
                            axios
                                .get(`${API_BASE}/portal/me`, { params: { t: token } })
                                .then((r) => setMe(r.data))
                                .catch(() => { /* keep view */ });
                        }}
                    />
                )}

                <footer className="mt-10 pb-6 text-center text-[11px] text-slate-400">
                    © {new Date().getFullYear()} {companyName}. Secure read-only portal.
                </footer>
            </main>
        </div>
    );
}

/* ---------- reusable pieces ---------- */
function MetricCard({ icon: Icon, label, value, sub, accent, testid }) {
    return (
        <Card className="rounded-2xl border-slate-100 p-5 hover:shadow-card-hover transition"
            data-testid={testid}
        >
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                    {label}
                </div>
                <div className="h-8 w-8 rounded-lg bg-brand-soft grid place-items-center">
                    <Icon className="h-4 w-4 text-brand-royal" strokeWidth={1.75} />
                </div>
            </div>
            <div className={`mt-3 num font-display font-bold text-2xl ${accent || "text-brand-navy"}`}>
                {value}
            </div>
            {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
        </Card>
    );
}

function LoanCard({ ln, token }) {
    const dueStatus = loanDueStatus(ln);
    const pending = pendingInterest(ln);
    const next = nextInstallment(ln);
    const isGroup = ln.loan_type === "group";
    // Additional Loan Reason & Group ID render only if present on the loan record.
    const additionalReason = ln.additional_loan_reason || ln.additional_reason || "";
    const groupId = ln.group_id || ln.group_code || "";
    const calcMethodLabel =
        ln.payment_mode === "interest_only"
            ? "Interest-only (principal on maturity)"
            : "Monthly EMI (reducing balance)";
    const frequencyLabel = "Monthly";
    return (
        <Card className="rounded-2xl border-slate-100 p-5 sm:p-6" data-testid={`portal-loan-${ln.id}`}>
            {/* header */}
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-display font-bold text-brand-navy text-lg capitalize">
                            {ln.loan_type} Loan
                        </div>
                        <span className="text-[10px] font-mono uppercase bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded"
                            data-testid={`portal-loan-code-${ln.id}`}
                        >
                            {ln.loan_code}
                        </span>
                        <Badge className={ln.status === "active"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-100"}
                            data-testid={`portal-loan-status-${ln.id}`}
                        >
                            {ln.status}
                        </Badge>
                        {dueStatus && (
                            <Badge
                                className={
                                    dueStatus.tone === "rose"
                                        ? "bg-rose-50 text-rose-700 border-rose-100 hover:bg-rose-50"
                                        : dueStatus.tone === "orange"
                                        ? "bg-orange-50 text-orange-700 border-orange-100 hover:bg-orange-50"
                                        : "bg-amber-50 text-amber-800 border-amber-100 hover:bg-amber-50"
                                }
                                data-testid={`portal-loan-due-status-${ln.id}`}
                            >
                                {dueStatus.label}
                                {dueStatus.kind === "due_soon"
                                    ? ` · ${dueStatus.days === 0 ? "today" : `in ${dueStatus.days}d`}`
                                    : ` · ${dueStatus.days}d`}
                            </Badge>
                        )}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                        {ln.interest_rate}% / month · {ln.tenure_months} months · {frequencyLabel}
                    </div>
                </div>
            </div>

            {/* Highlighted "Additional Loan Reason" pill if present */}
            {additionalReason && (
                <div className="mt-4 rounded-xl bg-brand-soft/70 border border-brand-soft p-3 flex items-start gap-2 text-xs text-brand-navy"
                    data-testid={`portal-loan-additional-reason-${ln.id}`}
                >
                    <Sparkles className="h-4 w-4 text-brand-royal mt-0.5 shrink-0" />
                    <div>
                        <div className="font-bold uppercase tracking-wider text-[10px] text-brand-royal">
                            Additional Loan · Reason
                        </div>
                        <div className="mt-0.5 leading-relaxed">{additionalReason}</div>
                    </div>
                </div>
            )}

            {/* Core details grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-5 pt-5 border-t border-slate-100">
                <Stat label="Loan A/C No." value={ln.loan_code} />
                <Stat label="Product" value={`${ln.loan_type[0].toUpperCase()}${ln.loan_type.slice(1)} Loan`} />
                <Stat label="Sanctioned Amount" value={formatINR(ln.principal)} />
                <Stat label="Disbursement Date" value={formatDate(ln.start_date)} />
                <Stat label="Tenure" value={`${ln.tenure_months} months`} />
                <Stat label="Repayment Frequency" value={frequencyLabel} />
                <Stat label="Interest Rate" value={`${ln.interest_rate}% / month`} />
                <Stat label="Calculation Method" value={calcMethodLabel} />
                <Stat label="Outstanding Principal" value={formatINR(ln.outstanding_principal)} accent />
                <Stat label="Pending Interest" value={formatINR(pending)} accent={pending > 0} />
                <Stat label="Next Due Date" value={next.date ? formatDate(next.date) : formatDate(ln.due_date)} />
                <Stat label="Next Due Amount" value={formatINR(next.amount || ln.emi)} />
                {isGroup && (
                    <Stat
                        label="Group ID"
                        value={groupId || "—"}
                    />
                )}
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
                <PdfButton
                    path={`/portal/passbook/loan/${ln.id}`}
                    token={token}
                    label="Passbook"
                    testid={`portal-passbook-${ln.id}`}
                />
                <PdfButton
                    path={`/portal/certificate/loan-sanction/${ln.id}`}
                    token={token}
                    label="Sanction Certificate"
                    testid={`portal-cert-${ln.id}`}
                />
            </div>
        </Card>
    );
}

function PaymentRow({ p }) {
    const [open, setOpen] = useState(false);
    const token = new URLSearchParams(window.location.search).get("t") || "";
    return (
        <div className="p-4 flex items-start justify-between gap-3 cursor-pointer hover:bg-slate-50"
            role="button" tabIndex={0}
            onClick={() => setOpen(true)}
            onKeyDown={(e) => { if (e.key === "Enter") setOpen(true); }}
            data-testid={`portal-payment-${p.id}`}>
            <EntryDetailDialog paymentId={p.id} portalToken={token} open={open} onClose={() => setOpen(false)} />
            <div className="min-w-0">
                <div className="text-xs text-slate-500">
                    {formatDateTime(p.collection_datetime || p.payment_date)} · <span className="uppercase">{p.mode}</span> · {p.staff || "—"}
                </div>
                {p.principal_paid > 0 && (
                    <div className="text-[11px] text-emerald-700 mt-0.5">
                        {formatINR(p.principal_paid)} deducted from principal
                    </div>
                )}
                {p.notes && <div className="text-xs text-slate-600 mt-1 italic truncate">{p.notes}</div>}
            </div>
            <div className="text-right shrink-0">
                <div className="num font-bold text-slate-900">{formatINR(p.total)}</div>
                {p.balance_after > 0 && (
                    <div className="text-[10px] text-slate-500">
                        Bal: {formatINR(p.balance_after)}
                    </div>
                )}
            </div>
        </div>
    );
}

function ProfileRow({ label, value, mono, full }) {
    return (
        <div className={full ? "sm:col-span-2" : ""}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                {label}
            </div>
            <div className={`mt-1 text-sm font-semibold text-slate-900 ${mono ? "font-mono" : ""}`}>
                {value}
            </div>
        </div>
    );
}





/* ---------- Security Settings ---------- */
function SecuritySettings({ customer, token, onUpdated }) {
    const [mode, setMode] = useState("password"); // password | pin | qa
    return (
        <div className="space-y-4" data-testid="portal-security">
            <Card className="rounded-2xl border-slate-100 p-4 sm:p-5">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-brand-royal font-semibold">
                            Account security
                        </div>
                        <h3 className="mt-1 font-display font-bold text-brand-navy text-lg">
                            Manage your credentials
                        </h3>
                        <p className="mt-1 text-xs text-slate-500 max-w-lg">
                            Update your password, PIN or recovery question. We&#39;ll ask for your current
                            password or PIN before saving. Only hashed values are stored.
                        </p>
                    </div>
                    <div className="h-10 w-10 rounded-xl bg-brand-soft grid place-items-center">
                        <ShieldCheck className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                    </div>
                </div>

                {/* Status summary */}
                <div className="mt-5 grid gap-3 sm:grid-cols-3">
                    <SecurityStatusTile
                        label="Password"
                        isSet={customer?.has_password}
                        updated={customer?.password_updated_at}
                    />
                    <SecurityStatusTile
                        label="Security PIN"
                        isSet={customer?.has_pin}
                        updated={customer?.pin_updated_at}
                    />
                    <SecurityStatusTile
                        label="Security Q&A"
                        isSet={customer?.has_security_qa}
                        updated={customer?.security_updated_at}
                    />
                </div>

                {/* Section switcher */}
                <div className="mt-5 flex flex-wrap gap-1.5 pt-4 border-t border-slate-100">
                    {[
                        { key: "password", label: "Change Password" },
                        { key: "pin", label: "Change PIN" },
                        { key: "qa", label: "Security Q&A" },
                    ].map((t) => {
                        const active = mode === t.key;
                        return (
                            <button
                                key={t.key}
                                type="button"
                                onClick={() => setMode(t.key)}
                                className={`h-9 rounded-full px-3.5 text-xs font-semibold border transition ${
                                    active
                                        ? "bg-brand-navy text-white border-brand-navy"
                                        : "bg-white text-slate-700 border-slate-200 hover:border-brand-royal"
                                }`}
                                data-testid={`portal-sec-tab-${t.key}`}
                            >
                                {t.label}
                            </button>
                        );
                    })}
                </div>
            </Card>

            {mode === "password" && (
                <SecurityForm
                    kind="password"
                    token={token}
                    customer={customer}
                    onUpdated={onUpdated}
                />
            )}
            {mode === "pin" && (
                <SecurityForm
                    kind="pin"
                    token={token}
                    customer={customer}
                    onUpdated={onUpdated}
                />
            )}
            {mode === "qa" && (
                <SecurityForm
                    kind="qa"
                    token={token}
                    customer={customer}
                    onUpdated={onUpdated}
                />
            )}
        </div>
    );
}

function SecurityStatusTile({ label, isSet, updated }) {
    return (
        <div className="rounded-xl border border-slate-100 p-3">
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                    {label}
                </div>
                <Badge className={`text-[10px] ${
                    isSet
                        ? "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50"
                        : "bg-amber-50 text-amber-800 border-amber-100 hover:bg-amber-50"
                }`}>
                    {isSet ? "Configured" : "Not set"}
                </Badge>
            </div>
            <div className="mt-1.5 text-xs text-slate-700">
                {updated ? (
                    <>Last updated <span className="font-semibold">{formatDate(updated)}</span></>
                ) : (
                    <span className="text-slate-400 italic">No update history</span>
                )}
            </div>
        </div>
    );
}

function SecurityForm({ kind, token, customer, onUpdated }) {
    // Confirmation credential the user provides to authorise the change.
    const [confirmType, setConfirmType] = useState(customer?.has_password ? "password" : "pin");
    const [confirmValue, setConfirmValue] = useState("");

    // Change-specific fields
    const [newPassword, setNewPassword] = useState("");
    const [newPassword2, setNewPassword2] = useState("");
    const [newPin, setNewPin] = useState("");
    const [newPin2, setNewPin2] = useState("");
    const [question, setQuestion] = useState(customer?.customer_security_question || "");
    const [answer, setAnswer] = useState("");

    const [submitting, setSubmitting] = useState(false);

    // Reset local fields whenever the kind switches to prevent stale values.
    useEffect(() => {
        setConfirmValue("");
        setNewPassword(""); setNewPassword2("");
        setNewPin(""); setNewPin2("");
        setAnswer("");
        setQuestion(customer?.customer_security_question || "");
    }, [kind, customer]);

    async function submit(e) {
        e?.preventDefault?.();

        // Validate change payload
        const payload = { t: token };
        if (confirmType === "password") payload.current_password = confirmValue;
        else payload.current_pin = confirmValue;
        if (!confirmValue) {
            toast.error(`Enter your current ${confirmType === "password" ? "password" : "PIN"}`);
            return;
        }

        if (kind === "password") {
            if (newPassword.length < 6) {
                toast.error("Password must be at least 6 characters");
                return;
            }
            if (newPassword !== newPassword2) {
                toast.error("New passwords do not match");
                return;
            }
            payload.new_password = newPassword;
        } else if (kind === "pin") {
            if (!/^\d{4,6}$/.test(newPin)) {
                toast.error("PIN must be 4–6 digits");
                return;
            }
            if (newPin !== newPin2) {
                toast.error("New PINs do not match");
                return;
            }
            payload.new_pin = newPin;
        } else if (kind === "qa") {
            if (!question.trim() || question.trim().length < 5) {
                toast.error("Security question must be at least 5 characters");
                return;
            }
            if (!answer.trim() || answer.trim().length < 2) {
                toast.error("Provide a security answer");
                return;
            }
            payload.security_question = question.trim();
            payload.security_answer = answer.trim();
        }

        setSubmitting(true);
        try {
            await axios.post(`${API_BASE}/portal/security/update`, payload);
            toast.success(
                kind === "password" ? "Password updated"
                : kind === "pin"    ? "PIN updated"
                :                     "Security question updated"
            );
            setConfirmValue("");
            setNewPassword(""); setNewPassword2("");
            setNewPin(""); setNewPin2("");
            setAnswer("");
            onUpdated?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update failed");
        } finally {
            setSubmitting(false);
        }
    }

    const title =
        kind === "password" ? "Change Password"
        : kind === "pin"    ? "Change Security PIN"
        :                     "Update Security Question & Answer";

    return (
        <Card className="rounded-2xl border-slate-100 p-5 sm:p-6" data-testid={`portal-sec-form-${kind}`}>
            <div className="text-[11px] uppercase tracking-widest text-brand-royal font-semibold">
                {title}
            </div>
            <form onSubmit={submit} className="mt-4 space-y-4">
                {/* Confirmation panel */}
                <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4">
                    <div className="text-xs font-semibold text-slate-700">
                        Confirm it&#39;s you
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                        {customer?.has_password && (
                            <button
                                type="button"
                                onClick={() => { setConfirmType("password"); setConfirmValue(""); }}
                                className={`h-8 rounded-full px-3 text-[11px] font-semibold border transition ${
                                    confirmType === "password"
                                        ? "bg-brand-navy text-white border-brand-navy"
                                        : "bg-white text-slate-700 border-slate-200 hover:border-brand-royal"
                                }`}
                                data-testid="portal-sec-confirm-password-tab"
                            >
                                Confirm with password
                            </button>
                        )}
                        {customer?.has_pin && (
                            <button
                                type="button"
                                onClick={() => { setConfirmType("pin"); setConfirmValue(""); }}
                                className={`h-8 rounded-full px-3 text-[11px] font-semibold border transition ${
                                    confirmType === "pin"
                                        ? "bg-brand-navy text-white border-brand-navy"
                                        : "bg-white text-slate-700 border-slate-200 hover:border-brand-royal"
                                }`}
                                data-testid="portal-sec-confirm-pin-tab"
                            >
                                Confirm with PIN
                            </button>
                        )}
                    </div>
                    <div className="mt-3">
                        <Label htmlFor="sec-confirm" className="text-xs font-semibold text-slate-600">
                            Current {confirmType === "password" ? "password" : "PIN"}
                        </Label>
                        <Input
                            id="sec-confirm"
                            type="password"
                            value={confirmValue}
                            onChange={(e) => setConfirmValue(e.target.value)}
                            className="mt-1.5 h-11 rounded-xl"
                            data-testid="portal-sec-confirm-value"
                            autoComplete="current-password"
                        />
                    </div>
                </div>

                {/* Change fields */}
                {kind === "password" && (
                    <div className="grid gap-4 md:grid-cols-2">
                        <div>
                            <Label htmlFor="sec-np" className="text-xs font-semibold text-slate-600">
                                New password
                            </Label>
                            <Input
                                id="sec-np"
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="mt-1.5 h-11 rounded-xl"
                                autoComplete="new-password"
                                data-testid="portal-sec-new-password"
                            />
                            <div className="mt-1 text-[11px] text-slate-500">
                                6–128 characters. Use letters, numbers and symbols.
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="sec-np2" className="text-xs font-semibold text-slate-600">
                                Confirm new password
                            </Label>
                            <Input
                                id="sec-np2"
                                type="password"
                                value={newPassword2}
                                onChange={(e) => setNewPassword2(e.target.value)}
                                className="mt-1.5 h-11 rounded-xl"
                                autoComplete="new-password"
                                data-testid="portal-sec-new-password-2"
                            />
                        </div>
                    </div>
                )}

                {kind === "pin" && (
                    <div className="grid gap-4 md:grid-cols-2">
                        <div>
                            <Label htmlFor="sec-pin" className="text-xs font-semibold text-slate-600">
                                New PIN (4–6 digits)
                            </Label>
                            <Input
                                id="sec-pin"
                                type="password"
                                inputMode="numeric"
                                value={newPin}
                                onChange={(e) => setNewPin(e.target.value.replace(/\D+/g, "").slice(0, 6))}
                                className="mt-1.5 h-11 rounded-xl num tracking-widest"
                                autoComplete="new-password"
                                data-testid="portal-sec-new-pin"
                            />
                        </div>
                        <div>
                            <Label htmlFor="sec-pin2" className="text-xs font-semibold text-slate-600">
                                Confirm new PIN
                            </Label>
                            <Input
                                id="sec-pin2"
                                type="password"
                                inputMode="numeric"
                                value={newPin2}
                                onChange={(e) => setNewPin2(e.target.value.replace(/\D+/g, "").slice(0, 6))}
                                className="mt-1.5 h-11 rounded-xl num tracking-widest"
                                autoComplete="new-password"
                                data-testid="portal-sec-new-pin-2"
                            />
                        </div>
                    </div>
                )}

                {kind === "qa" && (
                    <div className="grid gap-4">
                        <div>
                            <Label htmlFor="sec-q" className="text-xs font-semibold text-slate-600">
                                Security question
                            </Label>
                            <Input
                                id="sec-q"
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                                placeholder="e.g. What was the name of your first school?"
                                className="mt-1.5 h-11 rounded-xl"
                                maxLength={200}
                                data-testid="portal-sec-question"
                            />
                        </div>
                        <div>
                            <Label htmlFor="sec-a" className="text-xs font-semibold text-slate-600">
                                Answer
                            </Label>
                            <Input
                                id="sec-a"
                                type="password"
                                value={answer}
                                onChange={(e) => setAnswer(e.target.value)}
                                className="mt-1.5 h-11 rounded-xl"
                                maxLength={200}
                                data-testid="portal-sec-answer"
                            />
                            <div className="mt-1 text-[11px] text-slate-500">
                                Answers are hashed before storage and case-insensitive.
                            </div>
                        </div>
                    </div>
                )}

                <div className="pt-3 border-t border-slate-100 flex items-center justify-end">
                    <Button
                        type="submit"
                        disabled={submitting}
                        className="h-11 rounded-xl bg-brand-navy hover:bg-brand-royal"
                        data-testid={`portal-sec-submit-${kind}`}
                    >
                        {submitting ? "Saving…" : "Save changes"}
                    </Button>
                </div>
            </form>
        </Card>
    );
}



/* ---------- Calculators (reuse existing public formulas) ---------- */
const CALC_DISCLAIMER = "Illustrative only; final approved terms may differ.";

/* Reuses `LoanForm.jsx` EMI formula: reducing-balance EMI with rate given as
   percent-per-month (matches the admin loan sanction calculation). */
function emiMonthly(principal, ratePctMonthly, n) {
    const r = (Number(ratePctMonthly) || 0) / 100;
    const P = Number(principal) || 0;
    const N = Number(n) || 0;
    if (!P || !N) return 0;
    if (r === 0) return P / N;
    return (P * r * Math.pow(1 + r, N)) / (Math.pow(1 + r, N) - 1);
}

function Calculators() {
    const [tab, setTab] = useState("loan"); // loan | savings | investment

    const tabs = [
        { key: "loan",       label: "Loan EMI" },
        { key: "savings",    label: "Saving Scheme" },
        { key: "investment", label: "Investment" },
    ];

    return (
        <div className="space-y-4" data-testid="portal-calculators">
            <Card className="rounded-2xl border-slate-100 p-4 sm:p-5">
                <div className="flex flex-wrap gap-1.5" data-testid="portal-calc-tabs">
                    {tabs.map((t) => {
                        const active = tab === t.key;
                        return (
                            <button
                                key={t.key}
                                type="button"
                                onClick={() => setTab(t.key)}
                                className={`h-9 rounded-full px-3.5 text-xs font-semibold border transition ${
                                    active
                                        ? "bg-brand-navy text-white border-brand-navy"
                                        : "bg-white text-slate-700 border-slate-200 hover:border-brand-royal"
                                }`}
                                data-testid={`portal-calc-tab-${t.key}`}
                            >
                                {t.label}
                            </button>
                        );
                    })}
                </div>
            </Card>

            {tab === "loan" && <LoanEmiCalc />}
            {tab === "savings" && <DailySavingsCalc />}
            {tab === "investment" && <InvestmentCalc />}

            <div className="text-[11px] text-slate-500 italic pl-1" data-testid="portal-calc-disclaimer">
                {CALC_DISCLAIMER}
            </div>
        </div>
    );
}

function LoanEmiCalc() {
    const [principal, setPrincipal] = useState(100000);
    const [ratePct, setRatePct] = useState(2);     // percent per month (matches LoanForm)
    const [months, setMonths] = useState(12);

    const result = useMemo(() => {
        const emi = emiMonthly(principal, ratePct, months);
        const total = emi * (Number(months) || 0);
        const interest = total - (Number(principal) || 0);
        return { emi, total, interest };
    }, [principal, ratePct, months]);

    return (
        <Card className="rounded-2xl border-slate-100 p-5 sm:p-6" data-testid="portal-calc-loan">
            <div className="text-[11px] uppercase tracking-widest text-brand-royal font-semibold">
                Loan EMI Calculator
            </div>
            <h3 className="mt-1 font-display font-bold text-brand-navy text-lg">
                Estimate your monthly EMI
            </h3>
            <div className="mt-5 grid gap-4 md:grid-cols-3">
                <div>
                    <Label htmlFor="cl-p" className="text-xs font-semibold text-slate-600">Loan amount (₹)</Label>
                    <Input
                        id="cl-p"
                        type="number"
                        min={0}
                        value={principal}
                        onChange={(e) => setPrincipal(e.target.value)}
                        className="mt-1.5 h-11 rounded-xl num"
                        data-testid="portal-calc-loan-principal"
                    />
                </div>
                <div>
                    <Label htmlFor="cl-r" className="text-xs font-semibold text-slate-600">Interest rate (% per month)</Label>
                    <Input
                        id="cl-r"
                        type="number"
                        min={0}
                        step={0.1}
                        value={ratePct}
                        onChange={(e) => setRatePct(e.target.value)}
                        className="mt-1.5 h-11 rounded-xl num"
                        data-testid="portal-calc-loan-rate"
                    />
                </div>
                <div>
                    <Label htmlFor="cl-n" className="text-xs font-semibold text-slate-600">Tenure (months)</Label>
                    <Input
                        id="cl-n"
                        type="number"
                        min={1}
                        max={360}
                        value={months}
                        onChange={(e) => setMonths(e.target.value)}
                        className="mt-1.5 h-11 rounded-xl num"
                        data-testid="portal-calc-loan-months"
                    />
                </div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-3 pt-5 border-t border-slate-100">
                <ResultTile label="Monthly EMI" value={formatINR(Math.round(result.emi))} highlight
                    testid="portal-calc-loan-emi" />
                <ResultTile label="Total Interest" value={formatINR(Math.round(result.interest))}
                    testid="portal-calc-loan-interest" />
                <ResultTile label="Total Payable" value={formatINR(Math.round(result.total))}
                    testid="portal-calc-loan-total" />
            </div>
        </Card>
    );
}

function DailySavingsCalc() {
    /* Lot-wise simple interest — mirrors the withdrawal engine.
       Each deposit earns interest only for the actual days from its
       posting date until maturity, capped at 365 days.  No post-maturity
       interest.  Rate tiers: 0% <180d, 3% 180-364d, 5% at 365d. */
    const [amount, setAmount] = useState(100);
    const [frequency, setFrequency] = useState("Daily");
    const [days, setDays] = useState(200);

    const preview = useMemo(() => {
        const a = Math.max(0, Number(amount) || 0);
        const d = Math.max(0, Math.min(365, Number(days) || 0));
        const step = frequency === "Daily" ? 1 : frequency === "Weekly" ? 7 : 30;
        const N = Math.floor(d / step);          // number of deposit lots
        const D = d;                             // effective maturity day (0..365)
        const rate = d >= 365 ? 5 : d >= 180 ? 3 : 0;
        const deposited = a * N;
        // Lot i (i = 1..N) posted at day step*i earns (D - step*i) days of
        // interest.  Any lot posted AT or AFTER maturity contributes 0.
        let sumDays = 0;
        for (let i = 1; i <= N; i++) {
            const lotDay = step * i;
            if (lotDay >= D) break;              // no post-maturity interest
            sumDays += (D - lotDay);
        }
        const interest = (a * rate / 100) * (sumDays / 365);
        return {
            deposited,
            rate,
            interest,
            total: deposited + interest,
            phase: d >= 365 ? "Maturity" : d >= 180 ? "180–364 completed days" : "Before 180 completed days",
        };
    }, [amount, frequency, days]);

    return (
        <Card className="rounded-2xl border-slate-100 p-5 sm:p-6" data-testid="portal-calc-savings">
            <div className="text-[11px] uppercase tracking-widest text-brand-royal font-semibold">
                Saving Scheme Calculator
            </div>
            <h3 className="mt-1 font-display font-bold text-brand-navy text-lg">
                Project your savings maturity
            </h3>
            <div className="mt-5 grid gap-4 md:grid-cols-3">
                <div>
                    <Label htmlFor="cs-a" className="text-xs font-semibold text-slate-600">Contribution amount (₹)</Label>
                    <Input
                        id="cs-a"
                        type="number"
                        min={0}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="mt-1.5 h-11 rounded-xl num"
                        data-testid="portal-calc-savings-amount"
                    />
                </div>
                <div>
                    <Label className="text-xs font-semibold text-slate-600">Frequency</Label>
                    <div className="mt-1.5 grid grid-cols-3 gap-1.5">
                        {["Daily", "Weekly", "Monthly"].map((f) => (
                            <button
                                key={f}
                                type="button"
                                onClick={() => setFrequency(f)}
                                className={`h-11 rounded-xl border text-xs font-semibold transition ${
                                    frequency === f
                                        ? "bg-brand-navy text-white border-brand-navy"
                                        : "bg-white text-slate-700 border-slate-200 hover:border-brand-royal"
                                }`}
                                data-testid={`portal-calc-savings-freq-${f.toLowerCase()}`}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                </div>
                <div>
                    <Label htmlFor="cs-d" className="text-xs font-semibold text-slate-600">Days (max 365)</Label>
                    <Input
                        id="cs-d"
                        type="number"
                        min={0}
                        max={365}
                        value={days}
                        onChange={(e) => setDays(e.target.value)}
                        className="mt-1.5 h-11 rounded-xl num"
                        data-testid="portal-calc-savings-days"
                    />
                </div>
            </div>

            <div className="mt-5 pt-5 border-t border-slate-100 grid gap-3 sm:grid-cols-4">
                <ResultTile label="Phase" value={preview.phase} testid="portal-calc-savings-phase" />
                <ResultTile label="Total Deposited" value={formatINR(preview.deposited)}
                    testid="portal-calc-savings-deposited" />
                <ResultTile label={`Interest (${preview.rate}%)`} value={formatINR(Math.round(preview.interest))}
                    testid="portal-calc-savings-interest" />
                <ResultTile label="Estimated Maturity" value={formatINR(Math.round(preview.total))} highlight
                    testid="portal-calc-savings-total" />
            </div>
        </Card>
    );
}

function InvestmentCalc() {
    /* Mirrors PublicInvestment.jsx logic: 12% simple interest over 365 days. */
    const [principal, setPrincipal] = useState(100000);

    const preview = useMemo(() => {
        const p = Math.max(0, Number(principal) || 0);
        const interest = (p * 12) / 100;
        return { principal: p, interest, maturity: p + interest };
    }, [principal]);

    return (
        <Card className="rounded-2xl border-slate-100 p-5 sm:p-6" data-testid="portal-calc-investment">
            <div className="text-[11px] uppercase tracking-widest text-brand-royal font-semibold">
                Investment Calculator
            </div>
            <h3 className="mt-1 font-display font-bold text-brand-navy text-lg">
                Estimate maturity at 365 days
            </h3>
            <div className="mt-5 grid gap-4 md:grid-cols-2">
                <div>
                    <Label htmlFor="ci-p" className="text-xs font-semibold text-slate-600">Investment amount (₹)</Label>
                    <Input
                        id="ci-p"
                        type="number"
                        min={0}
                        value={principal}
                        onChange={(e) => setPrincipal(e.target.value)}
                        className="mt-1.5 h-11 rounded-xl num"
                        data-testid="portal-calc-investment-principal"
                    />
                    <p className="mt-2 text-[11px] text-slate-500">
                        Simple interest at 12% p.a. over a fixed 365-day tenure. Premature closure
                        returns principal only.
                    </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                    <ResultTile label="Tenure" value="365 days" testid="portal-calc-investment-tenure" />
                    <ResultTile label="Interest (12%)"
                        value={formatINR(Math.round(preview.interest))}
                        testid="portal-calc-investment-interest" />
                    <ResultTile label="Maturity Value"
                        value={formatINR(Math.round(preview.maturity))}
                        highlight testid="portal-calc-investment-maturity" />
                </div>
            </div>
        </Card>
    );
}

function ResultTile({ label, value, highlight, testid }) {
    return (
        <div
            className={`rounded-xl border p-3 ${
                highlight
                    ? "border-brand-royal/25 bg-brand-soft/40"
                    : "border-slate-100 bg-white"
            }`}
            data-testid={testid}
        >
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                {label}
            </div>
            <div className={`num mt-1 text-base font-bold ${
                highlight ? "text-brand-royal" : "text-slate-900"
            }`}>
                {value}
            </div>
        </div>
    );
}



/* ---------- Complaints & Support ---------- */
const COMPLAINT_CATEGORIES = ["Loan", "Saving Scheme", "Investment", "Payment", "Account", "General"];

/* Official branch contact — mirrors /contact-us public page constants. */
const BRANCH_PHONE = "+91 93592 86008";
const BRANCH_PHONE_TEL = "+919359286008";
const BRANCH_ADDRESS = "Sai Sanskar Apartment, Kachare Galli, Dange Chauk Road, Ishwarpur, 415409";

function Support({ settings, companyName, customer, token }) {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({ category: "General", subject: "", description: "" });

    async function load() {
        setLoading(true);
        try {
            const r = await axios.get(`${API_BASE}/portal/complaints`, { params: { t: token } });
            setTickets(Array.isArray(r.data) ? r.data : []);
        } catch {
            setTickets([]);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (token) load();
    }, [token]);

    async function submit(e) {
        e?.preventDefault?.();
        const subject = form.subject.trim();
        const description = form.description.trim();
        if (subject.length < 3) {
            toast.error("Subject must be at least 3 characters");
            return;
        }
        if (description.length < 5) {
            toast.error("Please describe the issue in a bit more detail");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API_BASE}/portal/complaints`, {
                t: token,
                category: form.category,
                subject,
                description,
            });
            toast.success("Complaint raised. We'll be in touch shortly.");
            setForm({ category: "General", subject: "", description: "" });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not raise complaint");
        } finally {
            setSubmitting(false);
        }
    }

    const statusStyle = (s) => {
        const map = {
            Open:          "bg-amber-50 text-amber-800 border-amber-100",
            Assigned:      "bg-blue-50 text-blue-800 border-blue-100",
            "In Progress": "bg-indigo-50 text-indigo-800 border-indigo-100",
            Waiting:       "bg-slate-100 text-slate-700 border-slate-200",
            Resolved:      "bg-emerald-50 text-emerald-700 border-emerald-100",
            Closed:        "bg-slate-100 text-slate-600 border-slate-200",
        };
        return map[s] || "bg-slate-100 text-slate-700 border-slate-200";
    };

    return (
        <div className="space-y-4" data-testid="portal-support">
            {/* Raise new complaint */}
            <Card className="rounded-2xl border-slate-100 p-5 sm:p-6">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-brand-royal font-semibold">
                            Raise a complaint
                        </div>
                        <h3 className="mt-1 font-display font-bold text-brand-navy text-lg">
                            How can we help, {(customer?.name || "").split(" ")[0] || "there"}?
                        </h3>
                        <p className="mt-1 text-xs text-slate-500">
                            Submit a ticket and our support team will respond as soon as possible.
                        </p>
                    </div>
                    <div className="h-10 w-10 rounded-xl bg-brand-soft grid place-items-center">
                        <LifeBuoy className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                    </div>
                </div>
                <form onSubmit={submit} className="mt-5 grid gap-4 md:grid-cols-2">
                    <div>
                        <Label htmlFor="cx-cat" className="text-xs font-semibold text-slate-600">Category</Label>
                        <select
                            id="cx-cat"
                            value={form.category}
                            onChange={(e) => setForm({ ...form, category: e.target.value })}
                            className="mt-1.5 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                            data-testid="portal-cx-category"
                        >
                            {COMPLAINT_CATEGORIES.map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <Label htmlFor="cx-sub" className="text-xs font-semibold text-slate-600">Subject</Label>
                        <Input
                            id="cx-sub"
                            value={form.subject}
                            onChange={(e) => setForm({ ...form, subject: e.target.value })}
                            placeholder="Short summary"
                            className="mt-1.5 h-11 rounded-xl"
                            data-testid="portal-cx-subject"
                            maxLength={200}
                        />
                    </div>
                    <div className="md:col-span-2">
                        <Label htmlFor="cx-desc" className="text-xs font-semibold text-slate-600">Description</Label>
                        <textarea
                            id="cx-desc"
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            placeholder="Please describe your concern"
                            rows={4}
                            className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm resize-none focus:border-brand-royal focus:outline-none focus:ring-1 focus:ring-brand-royal"
                            data-testid="portal-cx-description"
                            maxLength={4000}
                        />
                        <div className="mt-1 text-[11px] text-slate-400 text-right">
                            {form.description.length}/4000
                        </div>
                    </div>
                    <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-2 pt-1 border-t border-slate-100">
                        <div className="text-[11px] text-slate-500">
                            You&#39;ll receive a Ticket No. for tracking after submission.
                        </div>
                        <Button
                            type="submit"
                            disabled={submitting}
                            className="h-11 rounded-xl bg-brand-navy hover:bg-brand-royal"
                            data-testid="portal-cx-submit"
                        >
                            {submitting ? "Submitting…" : "Submit complaint"}
                        </Button>
                    </div>
                </form>
            </Card>

            {/* Complaint history */}
            <Card className="rounded-2xl border-slate-100 overflow-hidden">
                <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-brand-royal font-semibold">
                            My Tickets
                        </div>
                        <h3 className="mt-0.5 font-display font-bold text-slate-900 text-lg">
                            Complaint history
                        </h3>
                    </div>
                    <div className="text-xs text-slate-500">
                        {tickets.length} total
                    </div>
                </div>
                {loading ? (
                    <div className="p-10 text-center text-sm text-slate-400">Loading tickets…</div>
                ) : tickets.length === 0 ? (
                    <div className="p-10 text-center text-sm text-slate-400"
                        data-testid="portal-cx-empty">
                        You haven&#39;t raised any complaints yet.
                    </div>
                ) : (
                    <>
                        {/* Desktop */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                                    <tr>
                                        <th className="text-left px-4 py-3 font-semibold">Ticket No.</th>
                                        <th className="text-left px-4 py-3 font-semibold">Date</th>
                                        <th className="text-left px-4 py-3 font-semibold">Category</th>
                                        <th className="text-left px-4 py-3 font-semibold">Subject</th>
                                        <th className="text-left px-4 py-3 font-semibold">Status</th>
                                        <th className="text-left px-4 py-3 font-semibold">Final Resolution</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {tickets.map((t) => (
                                        <tr key={t.id}
                                            className="hover:bg-slate-50/60 align-top"
                                            data-testid={`portal-cx-ticket-${t.id}`}
                                        >
                                            <td className="px-4 py-3 text-[11px] font-mono uppercase text-slate-700 whitespace-nowrap">
                                                {t.ticket_no}
                                            </td>
                                            <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                                                {t.created_at ? formatDate(t.created_at) : "—"}
                                            </td>
                                            <td className="px-4 py-3">
                                                <Badge className="bg-brand-soft text-brand-royal border-brand-soft hover:bg-brand-soft">
                                                    {t.category}
                                                </Badge>
                                            </td>
                                            <td className="px-4 py-3 text-slate-800 max-w-xs">
                                                <div className="font-semibold truncate">{t.subject}</div>
                                                <div className="text-[11px] text-slate-500 line-clamp-2">
                                                    {t.description}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <Badge className={`${statusStyle(t.status)} hover:opacity-100`}>
                                                    {t.status}
                                                </Badge>
                                            </td>
                                            <td className="px-4 py-3 text-slate-700 max-w-sm">
                                                {t.final_resolution
                                                    ? <span className="text-slate-800">{t.final_resolution}</span>
                                                    : <span className="text-slate-400 italic">Awaiting resolution</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile */}
                        <div className="md:hidden divide-y divide-slate-100">
                            {tickets.map((t) => (
                                <div key={t.id} className="p-4"
                                    data-testid={`portal-cx-mticket-${t.id}`}>
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold text-slate-900 truncate">
                                                {t.subject}
                                            </div>
                                            <div className="text-[11px] text-slate-500 mt-0.5 font-mono uppercase">
                                                {t.ticket_no} · {t.created_at ? formatDate(t.created_at) : "—"}
                                            </div>
                                        </div>
                                        <Badge className={`${statusStyle(t.status)} hover:opacity-100 shrink-0`}>
                                            {t.status}
                                        </Badge>
                                    </div>
                                    <div className="mt-2 flex items-center gap-1.5">
                                        <Badge className="bg-brand-soft text-brand-royal border-brand-soft hover:bg-brand-soft text-[10px]">
                                            {t.category}
                                        </Badge>
                                    </div>
                                    <p className="mt-2 text-xs text-slate-600 line-clamp-3">
                                        {t.description}
                                    </p>
                                    {t.final_resolution ? (
                                        <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-[11px] text-emerald-800">
                                            <span className="font-semibold">Resolution: </span>{t.final_resolution}
                                        </div>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </Card>

            {/* Existing Get-in-touch cards kept intact */}
            <div className="grid gap-4 md:grid-cols-2">
                <Card className="rounded-2xl border-slate-100 p-6">
                    <div className="text-[11px] uppercase tracking-widest text-brand-royal font-semibold">
                        Get in touch
                    </div>
                    <h3 className="mt-2 font-display font-bold text-brand-navy text-lg">
                        Prefer to talk to us?
                    </h3>
                    <p className="mt-2 text-sm text-slate-600">
                        Call or message the branch anytime for urgent issues.
                    </p>
                    <div className="mt-4 space-y-2">
                        {(() => {
                            const phone = settings?.phone || BRANCH_PHONE_TEL;
                            return (
                                <>
                                    <a
                                        href={`tel:${phone}`}
                                        className="inline-flex w-full items-center justify-center gap-1.5 h-11 px-3 rounded-full bg-brand-navy hover:bg-brand-royal text-white text-sm font-semibold"
                                        data-testid="portal-support-call"
                                    >
                                        <Phone className="h-4 w-4" /> Call {BRANCH_PHONE}
                                    </a>
                                    <a
                                        href={waLink(phone, `Hi ${companyName}, I need support. Customer ID: ${customer?.customer_code || ""}`)}
                                        target="_blank" rel="noreferrer"
                                        className="inline-flex w-full items-center justify-center gap-1.5 h-11 px-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
                                        data-testid="portal-support-wa"
                                    >
                                        <MessageCircle className="h-4 w-4" /> WhatsApp branch
                                    </a>
                                </>
                            );
                        })()}
                    </div>
                </Card>
                <Card className="rounded-2xl border-slate-100 p-6">
                    <div className="text-[11px] uppercase tracking-widest text-brand-royal font-semibold">
                        Branch details
                    </div>
                    <div className="mt-3 text-sm text-slate-700 leading-relaxed" data-testid="portal-support-address">
                        {settings?.address || BRANCH_ADDRESS}
                    </div>
                    <div className="mt-2 text-xs text-slate-500">
                        Phone: <span className="font-semibold text-slate-700">{settings?.phone || BRANCH_PHONE}</span>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-100" data-testid="portal-branch-hours">
                        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                            Office Hours
                        </div>
                        <div className="mt-1 text-xs text-slate-700 leading-snug space-y-0.5">
                            {OFFICE_HOURS_LINES.map((l) => (
                                <div key={l}>{l}</div>
                            ))}
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
}


/* ---------- Notifications (derived from existing approved data) ---------- */
function Notifications({ customer, loans, pigmy, payments, token }) {
    const [invs, setInvs] = useState([]);
    const [withdraws, setWithdraws] = useState([]);
    const [loading, setLoading] = useState(true);

    // Local read-tracking key: scoped per customer id so multiple portal sessions don't collide.
    const READ_KEY = `wf.notifs.read.${customer?.id || "anon"}`;
    const [readSet, setReadSet] = useState(() => {
        try {
            const raw = window.localStorage.getItem(READ_KEY);
            return new Set(raw ? JSON.parse(raw) : []);
        } catch {
            return new Set();
        }
    });

    function persistRead(next) {
        setReadSet(new Set(next));
        try {
            window.localStorage.setItem(READ_KEY, JSON.stringify([...next]));
        } catch { /* noop */ }
    }

    function markRead(id) {
        const n = new Set(readSet);
        n.add(id);
        persistRead(n);
    }

    function markAllRead(ids) {
        const n = new Set(readSet);
        ids.forEach((id) => n.add(id));
        persistRead(n);
    }

    useEffect(() => {
        if (!token) return;
        setLoading(true);
        Promise.allSettled([
            axios.get(`${API_BASE}/portal/investments`, { params: { t: token } }),
            axios.get(`${API_BASE}/portal/withdraw-requests`, { params: { t: token } }),
        ]).then(([iRes, wRes]) => {
            setInvs(iRes.status === "fulfilled" ? (iRes.value.data?.investments || []) : []);
            setWithdraws(wRes.status === "fulfilled" ? (Array.isArray(wRes.value.data) ? wRes.value.data : []) : []);
        }).finally(() => setLoading(false));
    }, [token]);

    // Build derived notifications. Each row: {id, date, title, message, status, kind}
    const items = useMemo(() => {
        const out = [];
        const now = new Date();
        const iso = (d) => new Date(d).toISOString().slice(0, 10);

        // 1. Loan due/overdue
        for (const l of loans || []) {
            if (String(l.status || "").toLowerCase() !== "active") continue;
            if (!l.due_date) continue;
            const due = new Date(l.due_date);
            const daysDiff = Math.round((due - now) / (1000 * 60 * 60 * 24));
            if (l.is_overdue) {
                out.push({
                    id: `loan-overdue-${l.id}`,
                    date: l.due_date,
                    title: "Loan Overdue",
                    message: `Loan ${l.loan_code} is past due (${formatDate(l.due_date)}). Outstanding ₹${formatINR(l.outstanding_principal)}.`,
                    status: "Overdue",
                    kind: "loan",
                });
            } else if (daysDiff >= 0 && daysDiff <= 7) {
                out.push({
                    id: `loan-duesoon-${l.id}`,
                    date: iso(now),
                    title: "Loan Due Soon",
                    message: `Loan ${l.loan_code} is due in ${daysDiff} day${daysDiff === 1 ? "" : "s"} (${formatDate(l.due_date)}).`,
                    status: "Due Soon",
                    kind: "loan",
                });
            }
        }

        // 2. Payment approved (only within the last 30 days to keep the feed relevant)
        const cutoff = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        for (const p of payments || []) {
            if (!p.payment_date) continue;
            const dt = new Date(p.payment_date);
            if (dt < cutoff) continue;
            const amt = Math.abs(Number(p.total || 0));
            if (p.loan_id) {
                out.push({
                    id: `pay-loan-${p.id}`,
                    date: (p.payment_date || "").slice(0, 10),
                    title: "Payment Approved",
                    message: `EMI payment of ${formatINR(amt)} recorded. Receipt ${p.receipt_no || (p.id || "").slice(0, 8).toUpperCase()}.`,
                    status: "Approved",
                    kind: "payment",
                });
            } else if (p.pigmy_account_id && amt > 0 && String(p.type || "").toLowerCase() !== "withdrawal" && Number(p.total || 0) > 0) {
                out.push({
                    id: `pay-savings-${p.id}`,
                    date: (p.payment_date || "").slice(0, 10),
                    title: "Saving Scheme Deposit Approved",
                    message: `Deposit of ${formatINR(amt)} credited. Receipt ${p.receipt_no || (p.id || "").slice(0, 8).toUpperCase()}.`,
                    status: "Approved",
                    kind: "payment",
                });
            }
        }

        // 3. Saving Scheme withdrawal status
        for (const w of withdraws || []) {
            const s = String(w.status || "").toLowerCase();
            const stampMap = {
                pending: w.created_at || w.request_date,
                approved: w.approved_at || w.request_date,
                paid: w.paid_at || w.approved_at || w.request_date,
                rejected: w.rejected_at || w.request_date,
            };
            const stamp = stampMap[s] || w.created_at || w.request_date;
            const acct = w.account_code ? `A/C ${String(w.account_code).replace(/^PG/, "SS")}` : "";
            const amtMsg = `${formatINR(w.net_payout || w.principal || 0)}`;
            const map = {
                pending:  { title: "Withdrawal Request Submitted",   status: "Pending",  msg: `Your withdrawal request for ${amtMsg} (${acct}) is awaiting approval.` },
                approved: { title: "Withdrawal Approved",            status: "Approved", msg: `Your withdrawal for ${amtMsg} (${acct}) has been approved and is being processed.` },
                paid:     { title: "Withdrawal Paid",                status: "Paid",     msg: `Your withdrawal of ${amtMsg} (${acct}) has been paid out.` },
                rejected: { title: "Withdrawal Rejected",            status: "Rejected", msg: `Your withdrawal request for ${amtMsg} (${acct}) was rejected. Please contact support.` },
            };
            const cfg = map[s];
            if (!cfg) continue;
            out.push({
                id: `wd-${s}-${w.id}`,
                date: (stamp || "").slice(0, 10),
                title: cfg.title,
                message: cfg.msg,
                status: cfg.status,
                kind: "savings",
            });
        }

        // 4. Investment maturity / closure
        for (const inv of invs || []) {
            const s = inv.status;
            if (s === "Matured" || s === "Matured/Closed") {
                out.push({
                    id: `inv-matured-${inv.id}`,
                    date: (inv.maturity_date || "").slice(0, 10),
                    title: "Investment Matured",
                    message: `Investment ${inv.account_code} has matured. Maturity Payout ${formatINR(inv.estimated_maturity_amount)}.`,
                    status: "Matured",
                    kind: "investment",
                });
            } else if (s === "Prematurely Closed") {
                // Only surface a Closed notification when an actual approved payout
                // has been recorded. Use the latest payout's date, not the (future) maturity date.
                const payouts = (inv.transactions || [])
                    .filter((t) => String(t.type || "").toLowerCase() === "payout" && t.date)
                    .sort((a, b) => String(b.date).localeCompare(String(a.date)));
                if (payouts.length === 0) continue;
                out.push({
                    id: `inv-closed-${inv.id}`,
                    date: (payouts[0].date || "").slice(0, 10),
                    title: "Investment Closed",
                    message: `Investment ${inv.account_code} was closed early. Final Payout ${formatINR(inv.final_payout_amount || 0)}.`,
                    status: "Closed",
                    kind: "investment",
                });
            }
        }

        // Newest first.
        out.sort((a, b) => String(b.date).localeCompare(String(a.date)));
        return out;
    }, [loans, payments, withdraws, invs]);

    const unreadCount = items.filter((n) => !readSet.has(n.id)).length;

    const statusStyle = (s) => {
        const map = {
            Overdue:   "bg-rose-50 text-rose-700 border-rose-100",
            "Due Soon":"bg-amber-50 text-amber-800 border-amber-100",
            Pending:   "bg-amber-50 text-amber-800 border-amber-100",
            Approved:  "bg-emerald-50 text-emerald-700 border-emerald-100",
            Paid:      "bg-blue-50 text-blue-800 border-blue-100",
            Rejected:  "bg-rose-50 text-rose-700 border-rose-100",
            Matured:   "bg-blue-50 text-blue-800 border-blue-100",
            Closed:    "bg-slate-100 text-slate-700 border-slate-200",
        };
        return map[s] || "bg-slate-100 text-slate-700 border-slate-200";
    };

    return (
        <div className="space-y-4" data-testid="portal-notifications">
            <Card className="rounded-2xl border-slate-100 p-4 sm:p-5 flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-brand-royal font-semibold">
                        Activity
                    </div>
                    <h3 className="mt-1 font-display font-bold text-slate-900 text-lg">
                        Notifications
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                        {items.length} total {unreadCount > 0 && (
                            <>
                                · <span className="text-brand-royal font-semibold">{unreadCount} unread</span>
                            </>
                        )}
                    </p>
                </div>
                {unreadCount > 0 && (
                    <Button
                        variant="outline"
                        onClick={() => markAllRead(items.map((i) => i.id))}
                        className="h-9 rounded-lg text-xs"
                        data-testid="portal-notif-mark-all"
                    >
                        Mark all as read
                    </Button>
                )}
            </Card>

            {loading ? (
                <Card className="rounded-2xl border-slate-100 p-10 text-center text-sm text-slate-400">
                    Loading notifications…
                </Card>
            ) : items.length === 0 ? (
                <Card className="rounded-2xl border-slate-100 p-10 text-center text-sm text-slate-400"
                    data-testid="portal-notif-empty">
                    You&#39;re all caught up. No notifications right now.
                </Card>
            ) : (
                <div className="space-y-2">
                    {items.map((n) => {
                        const isRead = readSet.has(n.id);
                        return (
                            <Card
                                key={n.id}
                                className={`rounded-2xl border p-4 flex items-start gap-3 transition ${
                                    isRead
                                        ? "border-slate-100 bg-white"
                                        : "border-brand-royal/25 bg-brand-soft/40"
                                }`}
                                data-testid={`portal-notif-${n.id}`}
                            >
                                {/* Unread indicator */}
                                <div className="pt-2">
                                    <span
                                        className={`inline-block h-2 w-2 rounded-full ${
                                            isRead ? "bg-slate-200" : "bg-brand-royal animate-pulse"
                                        }`}
                                    />
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between flex-wrap gap-2">
                                        <div className="min-w-0">
                                            <div className={`text-sm font-semibold truncate ${
                                                isRead ? "text-slate-700" : "text-slate-900"
                                            }`}>
                                                {n.title}
                                            </div>
                                            <div className="text-[11px] text-slate-500 mt-0.5">
                                                {n.date ? formatDate(n.date) : "—"}
                                            </div>
                                        </div>
                                        <Badge className={`${statusStyle(n.status)} hover:opacity-100 shrink-0`}>
                                            {n.status}
                                        </Badge>
                                    </div>
                                    <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">
                                        {n.message}
                                    </p>
                                    {!isRead && (
                                        <button
                                            type="button"
                                            onClick={() => markRead(n.id)}
                                            className="mt-2 text-[11px] font-semibold text-brand-royal hover:underline"
                                            data-testid={`portal-notif-mark-${n.id}`}
                                        >
                                            Mark as read
                                        </button>
                                    )}
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}


/* ---------- Documents (portal-visible generated PDFs) ---------- */
function Documents({ loans, pigmy, token }) {
    const [category, setCategory] = useState("all"); // all | kyc | loan | savings | investment
    const [investments, setInvestments] = useState([]);
    const [kycItems, setKycItems] = useState([]);

    // Load investments + KYC document list for this customer (read-only).
    useEffect(() => {
        if (!token) return;
        axios
            .get(`${API_BASE}/portal/investments`, { params: { t: token } })
            .then((r) => setInvestments(r.data?.investments || []))
            .catch(() => setInvestments([]));
        axios
            .get(`${API_BASE}/portal/kyc-list`, { params: { t: token } })
            .then((r) => setKycItems(r.data?.items || []))
            .catch(() => setKycItems([]));
    }, [token]);

    // Build rows purely from existing portal endpoints — customer sees only
    // their own current/approved records, and cannot upload/edit/delete.
    const rows = useMemo(() => {
        const out = [];
        for (const l of loans || []) {
            if (String(l.status || "").toLowerCase() === "pending" || l.archived) continue;
            out.push({
                id: `${l.id}-sanction`,
                name: "Loan Sanction Certificate",
                category: "Loan",
                catKey: "loan",
                account: l.loan_code || "—",
                date: l.start_date || l.created_at || "",
                path: `/portal/certificate/loan-sanction/${l.id}`,
                filename: `sanction-${l.loan_code || l.id}.pdf`,
            });
            out.push({
                id: `${l.id}-passbook`,
                name: "Loan Passbook",
                category: "Loan",
                catKey: "loan",
                account: l.loan_code || "—",
                date: l.start_date || l.created_at || "",
                path: `/portal/passbook/loan/${l.id}`,
                filename: `loan-passbook-${l.loan_code || l.id}.pdf`,
            });
        }
        for (const a of pigmy || []) {
            const st = String(a.status || "").toLowerCase();
            if (st === "pending" || st === "rejected" || a.archived) continue;
            out.push({
                id: `${a.id}-passbook`,
                name: "Saving Scheme Passbook",
                category: "Saving Scheme",
                catKey: "savings",
                account: a.account_code || "—",
                date: a.start_date || a.created_at || "",
                path: `/portal/passbook/pigmy/${a.id}`,
                filename: `savings-passbook-${a.account_code || a.id}.pdf`,
            });
        }
        for (const inv of investments || []) {
            const st = String(inv.status || "").toLowerCase();
            if (st === "pending" || st === "rejected" || inv.archived) continue;
            const code = inv.investor_code || inv.id;
            out.push({
                id: `${inv.id}-inv-cert`,
                name: "Investment Certificate",
                category: "Investment",
                catKey: "investment",
                account: code,
                date: inv.start_date || inv.created_at || "",
                path: `/portal/certificate/investment/${inv.id}`,
                filename: `investment-${code}.pdf`,
            });
            out.push({
                id: `${inv.id}-inv-passbook`,
                name: "Investment Passbook / Statement",
                category: "Investment",
                catKey: "investment",
                account: code,
                date: inv.start_date || inv.created_at || "",
                path: `/portal/passbook/investment/${inv.id}`,
                filename: `investment-passbook-${code}.pdf`,
            });
            // Maturity certificate — only meaningful once fully matured.
            if (st === "matured" || inv.fully_paid) {
                out.push({
                    id: `${inv.id}-inv-maturity`,
                    name: "Investment Maturity Certificate",
                    category: "Investment",
                    catKey: "investment",
                    account: code,
                    date: inv.maturity_date || inv.start_date || "",
                    path: `/portal/certificate/investment/${inv.id}?maturity=true`,
                    filename: `investment-maturity-${code}.pdf`,
                });
            }
        }
        for (const k of kycItems || []) {
            out.push({
                id: `kyc-${k.kind}`,
                name: k.name,
                category: "KYC",
                catKey: "kyc",
                account: "—",
                date: "",
                path: k.download_path,
                filename: `${k.kind}.png`,
            });
        }
        out.sort((x, y) => String(y.date).localeCompare(String(x.date)));
        return out;
    }, [loans, pigmy, investments, kycItems]);

    const filtered = useMemo(() => (
        category === "all" ? rows : rows.filter((r) => r.catKey === category)
    ), [rows, category]);

    const cats = [
        { key: "all",         label: "All",            n: rows.length },
        { key: "kyc",         label: "KYC",            n: rows.filter((r) => r.catKey === "kyc").length },
        { key: "loan",        label: "Loan",           n: rows.filter((r) => r.catKey === "loan").length },
        { key: "savings",     label: "Saving Scheme",  n: rows.filter((r) => r.catKey === "savings").length },
        { key: "investment",  label: "Investment",     n: rows.filter((r) => r.catKey === "investment").length },
    ];

    const catBadge = (c) => {
        const map = {
            KYC:              "bg-amber-50 text-amber-800 border-amber-100",
            Loan:             "bg-blue-50 text-blue-800 border-blue-100",
            "Saving Scheme":  "bg-emerald-50 text-emerald-700 border-emerald-100",
            Investment:       "bg-brand-soft text-brand-royal border-brand-soft",
        };
        return (
            <Badge className={`${map[c] || "bg-slate-100 text-slate-700"} hover:opacity-100`}>
                {c}
            </Badge>
        );
    };

    function makeUrl(path) {
        return `${API_BASE}${path}${path.includes("?") ? "&" : "?"}t=${encodeURIComponent(token)}`;
    }

    return (
        <div className="space-y-4" data-testid="portal-documents">
            {/* Category chips */}
            <Card className="rounded-2xl border-slate-100 p-4 sm:p-5">
                <div className="flex flex-wrap gap-1.5" data-testid="portal-docs-filters">
                    {cats.map((c) => {
                        const active = category === c.key;
                        const disabled = c.n === 0 && c.key !== "all";
                        return (
                            <button
                                key={c.key}
                                type="button"
                                disabled={disabled}
                                onClick={() => setCategory(c.key)}
                                className={`h-9 rounded-full px-3.5 text-xs font-semibold border transition inline-flex items-center gap-1.5 ${
                                    active
                                        ? "bg-brand-navy text-white border-brand-navy"
                                        : disabled
                                        ? "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed"
                                        : "bg-white text-slate-700 border-slate-200 hover:border-brand-royal"
                                }`}
                                data-testid={`portal-docs-filter-${c.key}`}
                            >
                                {c.label}
                                <span className={`text-[10px] font-mono ${active ? "text-white/80" : "text-slate-400"}`}>
                                    {c.n}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </Card>

            {/* Cards grid */}
            {filtered.length === 0 ? (
                <Card className="rounded-2xl border-slate-100 p-10 text-center text-sm text-slate-400"
                    data-testid="portal-docs-empty">
                    No documents available in this category.
                </Card>
            ) : (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {filtered.map((r) => (
                        <Card
                            key={r.id}
                            className="rounded-2xl border-slate-100 p-4 flex flex-col justify-between gap-3 hover:border-brand-royal transition"
                            data-testid={`portal-doc-${r.id}`}
                        >
                            <div>
                                <div className="flex items-start justify-between gap-2">
                                    <div className="h-9 w-9 rounded-lg bg-brand-soft/70 grid place-items-center shrink-0">
                                        <FileText className="h-4.5 w-4.5 text-brand-royal" strokeWidth={1.75} />
                                    </div>
                                    {catBadge(r.category)}
                                </div>
                                <div className="mt-3">
                                    <div className="font-semibold text-slate-900 text-sm leading-tight">
                                        {r.name}
                                    </div>
                                    <div className="text-[11px] text-slate-500 mt-1 font-mono uppercase">
                                        A/C {r.account}
                                    </div>
                                    <div className="text-[11px] text-slate-500 mt-0.5">
                                        Uploaded {r.date ? formatDate(r.date) : "—"}
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-2 pt-1">
                                <a
                                    href={makeUrl(r.path)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center justify-center h-9 px-3 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:border-brand-royal flex-1"
                                    data-testid={`portal-doc-view-${r.id}`}
                                >
                                    View
                                </a>
                                <a
                                    href={makeUrl(r.path)}
                                    download={r.filename}
                                    className="inline-flex items-center justify-center gap-1 h-9 px-3 rounded-lg bg-brand-navy hover:bg-brand-royal text-xs font-semibold text-white flex-1"
                                    data-testid={`portal-doc-download-${r.id}`}
                                >
                                    <FileDown className="h-3.5 w-3.5" /> Download
                                </a>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}


/* ---------- Receipts (unified Loan + Saving Scheme + Investment) ---------- */
function Receipts({ payments, loans, pigmy, token }) {
    const [product, setProduct] = useState("all"); // all | loan | savings | investment
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    // Investment payouts must be fetched separately (portal/payments does not include them).
    const [invRows, setInvRows] = useState([]);
    const [invLoading, setInvLoading] = useState(false);

    useEffect(() => {
        if (!token) return;
        setInvLoading(true);
        axios
            .get(`${API_BASE}/portal/investments`, { params: { t: token } })
            .then((r) => {
                const invs = r.data?.investments || [];
                const rows = [];
                for (const inv of invs) {
                    for (const t of inv.transactions || []) {
                        if (!t.date) continue;
                        // Receipts show incoming transactions only — payouts belong in Statements.
                        if (String(t.type || "").toLowerCase() === "payout") continue;
                        rows.push({
                            id: t.id || `${inv.id}-${t.date}`,
                            date: t.date,
                            receipt_no: t.reference || "",
                            product: "Investment",
                            account_code: inv.account_code || "",
                            amount: Math.abs(Number(t.amount || 0)),
                            direction: "Deposit",
                            mode: "—",
                            status: "Approved",
                            _key: "investment",
                        });
                    }
                }
                setInvRows(rows);
            })
            .catch(() => setInvRows([]))
            .finally(() => setInvLoading(false));
    }, [token]);

    // Build Loan + Saving Scheme rows from the already-loaded /portal/payments list.
    const paymentRows = useMemo(() => {
        const loanMap = new Map((loans || []).map((l) => [l.id, l]));
        const pigMap = new Map((pigmy || []).map((a) => [a.id, a]));
        const rows = [];
        for (const p of payments || []) {
            const status = String(p.status || "collected").toLowerCase();
            if (status === "pending" || status === "rejected") continue;
            const amt = Number(p.total || 0);
            if (p.loan_id) {
                const ln = loanMap.get(p.loan_id) || {};
                rows.push({
                    id: p.id,
                    date: (p.payment_date || "").slice(0, 10),
                    receipt_no: p.receipt_no || p.reference_no || (p.id || "").slice(0, 8).toUpperCase(),
                    product: "Loan",
                    account_code: ln.loan_code || "—",
                    amount: Math.abs(amt),
                    direction: "Repayment",
                    mode: (p.mode || "—").toUpperCase(),
                    status: "Approved",
                    _key: "loan",
                });
            } else if (p.pigmy_account_id) {
                const a = pigMap.get(p.pigmy_account_id) || {};
                const isWithdrawal = amt < 0 || String(p.type || "").toLowerCase() === "withdrawal";
                // Receipts show incoming transactions only — withdrawals belong in Statements.
                if (isWithdrawal) continue;
                rows.push({
                    id: p.id,
                    date: (p.payment_date || "").slice(0, 10),
                    receipt_no: p.receipt_no || p.reference_no || (p.id || "").slice(0, 8).toUpperCase(),
                    product: "Saving Scheme",
                    account_code: a.account_code || "—",
                    amount: Math.abs(amt),
                    direction: "Deposit",
                    mode: (p.mode || "—").toUpperCase(),
                    status: "Approved",
                    _key: "savings",
                });
            }
        }
        return rows;
    }, [payments, loans, pigmy]);

    const allRows = useMemo(() => {
        const combined = [...paymentRows, ...invRows];
        combined.sort((a, b) => String(b.date).localeCompare(String(a.date)));
        return combined;
    }, [paymentRows, invRows]);

    const filtered = useMemo(() => {
        return allRows.filter((r) => {
            if (product !== "all" && r._key !== product) return false;
            const d = (r.date || "").slice(0, 10);
            if (fromDate && d < fromDate) return false;
            if (toDate && d > toDate) return false;
            return true;
        });
    }, [allRows, product, fromDate, toDate]);

    const productPills = [
        { key: "all",         label: "All Products" },
        { key: "loan",        label: "Loan" },
        { key: "savings",     label: "Saving Scheme" },
        { key: "investment",  label: "Investment" },
    ];

    const productBadge = (p) => {
        const map = {
            Loan:            "bg-blue-50 text-blue-800 border-blue-100",
            "Saving Scheme": "bg-emerald-50 text-emerald-700 border-emerald-100",
            Investment:      "bg-brand-soft text-brand-royal border-brand-soft",
        };
        return (
            <Badge className={`${map[p] || "bg-slate-100 text-slate-700"} hover:opacity-100`}>
                {p}
            </Badge>
        );
    };

    return (
        <div className="space-y-4" data-testid="portal-receipts">
            {/* Filters */}
            <Card className="rounded-2xl border-slate-100 p-4 sm:p-5">
                <div className="flex flex-wrap items-center gap-1.5" data-testid="portal-receipts-filters">
                    {productPills.map((p) => {
                        const active = product === p.key;
                        return (
                            <button
                                key={p.key}
                                type="button"
                                onClick={() => setProduct(p.key)}
                                className={`h-9 rounded-full px-3.5 text-xs font-semibold border transition ${
                                    active
                                        ? "bg-brand-navy text-white border-brand-navy"
                                        : "bg-white text-slate-700 border-slate-200 hover:border-brand-royal"
                                }`}
                                data-testid={`portal-receipts-filter-${p.key}`}
                            >
                                {p.label}
                            </button>
                        );
                    })}
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-4">
                    <div>
                        <Label htmlFor="rc-from" className="text-[11px] font-semibold text-slate-600">From</Label>
                        <Input
                            id="rc-from"
                            type="date"
                            value={fromDate}
                            max={toDate || undefined}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="mt-1.5 h-10 rounded-xl"
                            data-testid="portal-receipts-from"
                        />
                    </div>
                    <div>
                        <Label htmlFor="rc-to" className="text-[11px] font-semibold text-slate-600">To</Label>
                        <Input
                            id="rc-to"
                            type="date"
                            value={toDate}
                            min={fromDate || undefined}
                            onChange={(e) => setToDate(e.target.value)}
                            className="mt-1.5 h-10 rounded-xl"
                            data-testid="portal-receipts-to"
                        />
                    </div>
                    {(fromDate || toDate) && (
                        <div className="flex items-end">
                            <Button
                                variant="outline"
                                onClick={() => { setFromDate(""); setToDate(""); }}
                                className="h-10 rounded-xl"
                                data-testid="portal-receipts-clear"
                            >
                                Clear dates
                            </Button>
                        </div>
                    )}
                </div>
            </Card>

            {/* Table */}
            <Card className="rounded-2xl border-slate-100 overflow-hidden">
                {invLoading && filtered.length === 0 ? (
                    <div className="p-10 text-center text-sm text-slate-400">Loading receipts…</div>
                ) : filtered.length === 0 ? (
                    <div className="p-10 text-center text-sm text-slate-400" data-testid="portal-receipts-empty">
                        No approved receipts for the selected filters.
                    </div>
                ) : (
                    <>
                        {/* Desktop table */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                                    <tr>
                                        <th className="text-left px-4 py-3 font-semibold">Date</th>
                                        <th className="text-left px-4 py-3 font-semibold">Receipt No.</th>
                                        <th className="text-left px-4 py-3 font-semibold">Product</th>
                                        <th className="text-left px-4 py-3 font-semibold">Account No.</th>
                                        <th className="text-right px-4 py-3 font-semibold">Amount (₹)</th>
                                        <th className="text-left px-4 py-3 font-semibold">Mode</th>
                                        <th className="text-left px-4 py-3 font-semibold">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {filtered.map((r) => (
                                        <tr key={`${r._key}-${r.id}`}
                                            className="hover:bg-slate-50/60"
                                            data-testid={`portal-receipt-${r.id}`}
                                        >
                                            <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                                                {r.date ? formatDate(r.date) : "—"}
                                            </td>
                                            <td className="px-4 py-3 text-[11px] font-mono uppercase text-slate-600">
                                                {r.receipt_no || "—"}
                                            </td>
                                            <td className="px-4 py-3">{productBadge(r.product)}</td>
                                            <td className="px-4 py-3 text-[11px] font-mono uppercase text-slate-700">
                                                {r.account_code || "—"}
                                            </td>
                                            <td className={`px-4 py-3 text-right num font-semibold ${
                                                r.direction === "Withdrawal" || r.direction === "Payout"
                                                    ? "text-rose-700"
                                                    : "text-emerald-700"
                                            }`}>
                                                {r.direction === "Withdrawal" || r.direction === "Payout" ? "- " : "+ "}
                                                {formatINR(r.amount)}
                                            </td>
                                            <td className="px-4 py-3 text-xs text-slate-600 uppercase">
                                                {r.mode}
                                            </td>
                                            <td className="px-4 py-3">
                                                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50">
                                                    {r.status}
                                                </Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile stacked cards */}
                        <div className="md:hidden divide-y divide-slate-100">
                            {filtered.map((r) => (
                                <div key={`${r._key}-${r.id}`} className="p-4"
                                    data-testid={`portal-receipt-m-${r.id}`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold text-slate-800 truncate">
                                                {r.direction} · {r.account_code}
                                            </div>
                                            <div className="text-[11px] text-slate-500 mt-0.5">
                                                {formatDate(r.date)} · <span className="font-mono uppercase">{r.receipt_no || "—"}</span>
                                            </div>
                                            <div className="mt-1.5 flex items-center gap-1.5">
                                                {productBadge(r.product)}
                                                <Badge className="bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-100 text-[10px] uppercase">
                                                    {r.mode}
                                                </Badge>
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className={`num text-sm font-bold ${
                                                r.direction === "Withdrawal" || r.direction === "Payout"
                                                    ? "text-rose-700" : "text-emerald-700"
                                            }`}>
                                                {r.direction === "Withdrawal" || r.direction === "Payout" ? "- " : "+ "}
                                                {formatINR(r.amount)}
                                            </div>
                                            <Badge className="mt-1 bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50 text-[10px]">
                                                {r.status}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
}


/* ---------- Statements (Loan / Saving Scheme / Investment ledger) ---------- */
function Statements({ loans, pigmy, token }) {
    const [kind, setKind] = useState("loan"); // "loan" | "savings" | "investment"

    // Loan/savings account selector (defaults to first). Investment is aggregate-per-account.
    const loanOptions = useMemo(() => (loans || []).map((l) => ({
        id: l.id, code: l.loan_code, label: `${(l.loan_type || "loan")} · ${l.loan_code}`,
        principal: Number(l.principal || 0), start_date: l.start_date,
    })), [loans]);
    const savingsOptions = useMemo(() => (pigmy || []).map((a) => ({
        id: a.id, code: a.account_code, label: `Saving Scheme · ${a.account_code}`,
    })), [pigmy]);

    const [selLoanId, setSelLoanId] = useState("");
    const [selSavingsId, setSelSavingsId] = useState("");
    const [selInvId, setSelInvId] = useState("");

    useEffect(() => { if (!selLoanId && loanOptions.length) setSelLoanId(loanOptions[0].id); }, [loanOptions, selLoanId]);
    useEffect(() => { if (!selSavingsId && savingsOptions.length) setSelSavingsId(savingsOptions[0].id); }, [savingsOptions, selSavingsId]);

    // Date filter (yyyy-mm-dd strings). Empty = no bound.
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    // Data caches keyed by tab.
    const [loanRows, setLoanRows] = useState([]);
    const [savingsRows, setSavingsRows] = useState([]);
    const [investments, setInvestments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    // Loan statement: /portal/payments (all approved loan+pigmy pays); filter to selected loan.
    useEffect(() => {
        if (kind !== "loan" || !selLoanId) return;
        setLoading(true);
        setErr("");
        axios
            .get(`${API_BASE}/portal/payments`, { params: { t: token } })
            .then((r) => {
                const loan = loanOptions.find((l) => l.id === selLoanId) || {};
                const pays = (Array.isArray(r.data) ? r.data : [])
                    .filter((p) => p.loan_id === selLoanId)
                    .filter((p) => String(p.status || "collected").toLowerCase() !== "pending"
                                && String(p.status || "collected").toLowerCase() !== "rejected")
                    .sort((a, b) => String(a.payment_date).localeCompare(String(b.payment_date)));
                const rows = [];
                // Opening: loan disbursed increases outstanding → Debit
                if (loan.start_date && Number(loan.principal) > 0) {
                    rows.push({
                        date: loan.start_date,
                        description: "Loan Disbursed",
                        debit: Number(loan.principal),
                        credit: 0,
                        balance: Number(loan.principal),
                        reference: loan.code || "",
                    });
                }
                for (const p of pays) {
                    const paid = Number(p.total || (Number(p.principal_paid || 0) + Number(p.interest_paid || 0)) || 0);
                    // Approved repayment reduces outstanding → Credit
                    rows.push({
                        date: (p.payment_date || "").slice(0, 10),
                        description: `EMI Payment${p.mode ? ` (${p.mode})` : ""}`,
                        debit: 0,
                        credit: paid,
                        balance: Number(p.balance_after ?? 0),
                        reference: p.receipt_no || p.reference_no || (p.id || "").slice(0, 8).toUpperCase(),
                    });
                }
                setLoanRows(rows);
            })
            .catch((e) => setErr(e?.response?.data?.detail || "Could not load statement"))
            .finally(() => setLoading(false));
    }, [kind, selLoanId, token, loanOptions]);

    // Daily savings statement: /portal/savings/entries/{aid}
    useEffect(() => {
        if (kind !== "savings" || !selSavingsId) return;
        setLoading(true);
        setErr("");
        axios
            .get(`${API_BASE}/portal/savings/entries/${selSavingsId}`, { params: { t: token } })
            .then((r) => {
                const entries = r.data?.entries || [];
                const rows = entries.map((e) => {
                    const amt = Number(e.amount || 0);
                    const isWithdraw = amt < 0;
                    return {
                        date: e.posting_date || "",
                        description: isWithdraw
                            ? "Withdrawal (Paid)"
                            : `Deposit${e.type && e.type !== "Scheduled" ? ` (${e.type})` : ""}`,
                        debit: isWithdraw ? Math.abs(amt) : 0,
                        credit: isWithdraw ? 0 : amt,
                        balance: Number(e.running_balance ?? 0),
                        reference: e.reference || "",
                    };
                });
                setSavingsRows(rows);
            })
            .catch((e) => setErr(e?.response?.data?.detail || "Could not load statement"))
            .finally(() => setLoading(false));
    }, [kind, selSavingsId, token]);

    // Investment statement: /portal/investments (returns all investments with transactions).
    useEffect(() => {
        if (kind !== "investment") return;
        setLoading(true);
        setErr("");
        axios
            .get(`${API_BASE}/portal/investments`, { params: { t: token } })
            .then((r) => {
                const invs = r.data?.investments || [];
                setInvestments(invs);
                if (!selInvId && invs.length) setSelInvId(invs[0].id);
            })
            .catch((e) => setErr(e?.response?.data?.detail || "Could not load statement"))
            .finally(() => setLoading(false));
    }, [kind, token]);

    const investmentRows = useMemo(() => {
        if (kind !== "investment") return [];
        const inv = investments.find((i) => i.id === selInvId) || investments[0];
        if (!inv) return [];
        const txns = [...(inv.transactions || [])].sort(
            (a, b) => String(a.date || "").localeCompare(String(b.date || "")),
        );
        let bal = 0;
        return txns.map((t) => {
            const amt = Number(t.amount || 0);
            const isPayout = String(t.type || "").toLowerCase() === "payout";
            if (isPayout) bal = Math.max(0, bal - amt); else bal += amt;
            return {
                date: t.date || "",
                description: isPayout ? "Payout Received" : "Investment Deposit",
                debit: isPayout ? amt : 0,
                credit: isPayout ? 0 : amt,
                balance: bal,
                reference: t.reference || "",
            };
        });
    }, [kind, investments, selInvId]);

    // Choose active dataset + PDF path
    const raw = kind === "loan" ? loanRows : kind === "savings" ? savingsRows : investmentRows;
    const pdfPath = kind === "loan" && selLoanId
        ? `/portal/passbook/loan/${selLoanId}`
        : kind === "savings" && selSavingsId
        ? `/portal/passbook/pigmy/${selSavingsId}`
        : null; // No portal PDF endpoint for investment — skip per instructions

    // Apply date filter
    const filtered = useMemo(() => {
        const from = fromDate || "";
        const to = toDate || "";
        return raw.filter((r) => {
            const d = (r.date || "").slice(0, 10);
            if (from && d < from) return false;
            if (to && d > to) return false;
            return true;
        });
    }, [raw, fromDate, toDate]);

    const totals = useMemo(() => filtered.reduce(
        (a, r) => ({ debit: a.debit + Number(r.debit || 0), credit: a.credit + Number(r.credit || 0) }),
        { debit: 0, credit: 0 },
    ), [filtered]);

    const tabs = [
        { key: "loan", label: "Loan Statement", disabled: loanOptions.length === 0 },
        { key: "savings", label: "Saving Scheme Statement", disabled: savingsOptions.length === 0 },
        { key: "investment", label: "Investment Statement" },
    ];

    return (
        <div className="space-y-4" data-testid="portal-statements">
            <Card className="rounded-2xl border-slate-100 p-4 sm:p-5">
                {/* Tabs */}
                <div className="flex flex-wrap gap-1.5" data-testid="portal-stmt-tabs">
                    {tabs.map((t) => {
                        const active = kind === t.key;
                        return (
                            <button
                                key={t.key}
                                type="button"
                                disabled={t.disabled}
                                onClick={() => setKind(t.key)}
                                className={`h-9 rounded-full px-3.5 text-xs font-semibold border transition ${
                                    active
                                        ? "bg-brand-navy text-white border-brand-navy"
                                        : t.disabled
                                        ? "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed"
                                        : "bg-white text-slate-700 border-slate-200 hover:border-brand-royal"
                                }`}
                                data-testid={`portal-stmt-tab-${t.key}`}
                            >
                                {t.label}
                            </button>
                        );
                    })}
                </div>

                {/* Controls row: account selector + date filter + PDF */}
                <div className="mt-4 grid gap-3 md:grid-cols-4">
                    <div>
                        <Label className="text-[11px] font-semibold text-slate-600">Account</Label>
                        {kind === "loan" && (
                            <select
                                value={selLoanId}
                                onChange={(e) => setSelLoanId(e.target.value)}
                                className="mt-1.5 h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm capitalize"
                                data-testid="portal-stmt-account-loan"
                            >
                                {loanOptions.map((o) => (
                                    <option key={o.id} value={o.id}>{o.label}</option>
                                ))}
                                {loanOptions.length === 0 && <option value="">No loans</option>}
                            </select>
                        )}
                        {kind === "savings" && (
                            <select
                                value={selSavingsId}
                                onChange={(e) => setSelSavingsId(e.target.value)}
                                className="mt-1.5 h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                                data-testid="portal-stmt-account-savings"
                            >
                                {savingsOptions.map((o) => (
                                    <option key={o.id} value={o.id}>{o.label}</option>
                                ))}
                                {savingsOptions.length === 0 && <option value="">No accounts</option>}
                            </select>
                        )}
                        {kind === "investment" && (
                            <select
                                value={selInvId}
                                onChange={(e) => setSelInvId(e.target.value)}
                                className="mt-1.5 h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                                data-testid="portal-stmt-account-investment"
                            >
                                {investments.map((i) => (
                                    <option key={i.id} value={i.id}>
                                        Investment · {i.account_code}
                                    </option>
                                ))}
                                {investments.length === 0 && <option value="">No investments</option>}
                            </select>
                        )}
                    </div>
                    <div>
                        <Label htmlFor="stmt-from" className="text-[11px] font-semibold text-slate-600">From</Label>
                        <Input
                            id="stmt-from"
                            type="date"
                            value={fromDate}
                            max={toDate || undefined}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="mt-1.5 h-10 rounded-xl"
                            data-testid="portal-stmt-from"
                        />
                    </div>
                    <div>
                        <Label htmlFor="stmt-to" className="text-[11px] font-semibold text-slate-600">To</Label>
                        <Input
                            id="stmt-to"
                            type="date"
                            value={toDate}
                            min={fromDate || undefined}
                            onChange={(e) => setToDate(e.target.value)}
                            className="mt-1.5 h-10 rounded-xl"
                            data-testid="portal-stmt-to"
                        />
                    </div>
                    <div className="flex items-end gap-2">
                        {(fromDate || toDate) && (
                            <Button
                                variant="outline"
                                onClick={() => { setFromDate(""); setToDate(""); }}
                                className="h-10 rounded-xl"
                                data-testid="portal-stmt-clear"
                            >
                                Clear
                            </Button>
                        )}
                        {pdfPath && (
                            <PdfButton
                                path={pdfPath}
                                token={token}
                                label="Download PDF"
                                testid="portal-stmt-pdf"
                            />
                        )}
                    </div>
                </div>
            </Card>

            {/* Ledger table */}
            <Card className="rounded-2xl border-slate-100 overflow-hidden" data-testid="portal-stmt-table">
                {err ? (
                    <div className="p-8 text-center text-sm text-rose-600">{err}</div>
                ) : loading ? (
                    <div className="p-10 text-center text-sm text-slate-400">Loading statement…</div>
                ) : filtered.length === 0 ? (
                    <div className="p-10 text-center text-sm text-slate-400"
                        data-testid="portal-stmt-empty">
                        No transactions found for the selected period.
                    </div>
                ) : (
                    <>
                        {/* Desktop table */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                                    <tr>
                                        <th className="text-left px-4 py-3 font-semibold">Date</th>
                                        <th className="text-left px-4 py-3 font-semibold">Description</th>
                                        <th className="text-right px-4 py-3 font-semibold">Debit (₹)</th>
                                        <th className="text-right px-4 py-3 font-semibold">Credit (₹)</th>
                                        <th className="text-right px-4 py-3 font-semibold">Balance (₹)</th>
                                        <th className="text-left px-4 py-3 font-semibold">Reference No.</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {filtered.map((r, i) => (
                                        <tr key={i} data-testid={`portal-stmt-row-${i}`}
                                            className="hover:bg-slate-50/60">
                                            <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                                                {r.date ? formatDate(r.date) : "—"}
                                            </td>
                                            <td className="px-4 py-3 text-slate-800">{r.description}</td>
                                            <td className="px-4 py-3 text-right num text-rose-700">
                                                {r.debit ? formatINR(r.debit) : "—"}
                                            </td>
                                            <td className="px-4 py-3 text-right num text-emerald-700">
                                                {r.credit ? formatINR(r.credit) : "—"}
                                            </td>
                                            <td className="px-4 py-3 text-right num font-semibold text-slate-900">
                                                {formatINR(r.balance)}
                                            </td>
                                            <td className="px-4 py-3 text-[11px] font-mono uppercase text-slate-600">
                                                {r.reference || "—"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="bg-slate-50 border-t border-slate-200 text-[11px] uppercase text-slate-600 font-semibold">
                                        <td colSpan="2" className="px-4 py-3 text-right">Totals</td>
                                        <td className="px-4 py-3 text-right num text-rose-700"
                                            data-testid="portal-stmt-total-debit">
                                            {formatINR(totals.debit)}
                                        </td>
                                        <td className="px-4 py-3 text-right num text-emerald-700"
                                            data-testid="portal-stmt-total-credit">
                                            {formatINR(totals.credit)}
                                        </td>
                                        <td colSpan="2"></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        {/* Mobile stacked cards */}
                        <div className="md:hidden divide-y divide-slate-100">
                            {filtered.map((r, i) => (
                                <div key={i} className="p-4"
                                    data-testid={`portal-stmt-mrow-${i}`}>
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold text-slate-800 truncate">
                                                {r.description}
                                            </div>
                                            <div className="text-[11px] text-slate-500 mt-0.5">
                                                {r.date ? formatDate(r.date) : "—"}
                                                {r.reference ? ` · ${r.reference}` : ""}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className={`num text-sm font-bold ${r.debit ? "text-rose-700" : "text-emerald-700"}`}>
                                                {r.debit ? `- ${formatINR(r.debit)}` : `+ ${formatINR(r.credit)}`}
                                            </div>
                                            <div className="text-[11px] text-slate-500 mt-0.5 num">
                                                Bal {formatINR(r.balance)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <div className="p-3 bg-slate-50 flex items-center justify-between text-[11px] uppercase text-slate-600 font-semibold">
                                <span>Totals</span>
                                <span className="num">
                                    Dr {formatINR(totals.debit)} · Cr {formatINR(totals.credit)}
                                </span>
                            </div>
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
}



/* ---------- Investment ---------- */
function Investment({ token }) {
    const [items, setItems] = useState(null);
    const [selectedId, setSelectedId] = useState("");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (!token) return;
        setLoading(true);
        setErr("");
        axios
            .get(`${API_BASE}/portal/investments`, { params: { t: token } })
            .then((r) => {
                const list = r.data.investments || [];
                setItems(list);
                if (list.length && !selectedId) setSelectedId(list[0].id);
            })
            .catch((e) => setErr(e?.response?.data?.detail || "Could not load investments"))
            .finally(() => setLoading(false));
    }, [token]);

    if (loading) {
        return (
            <Card className="rounded-2xl border-slate-100 p-10 text-center text-sm text-slate-400"
                data-testid="portal-investments">
                Loading investments…
            </Card>
        );
    }
    if (err) {
        return (
            <Card className="rounded-2xl border-rose-100 bg-rose-50 p-6 text-sm text-rose-700"
                data-testid="portal-investments-error">
                {err}
            </Card>
        );
    }
    if (!items || items.length === 0) {
        return (
            <Card className="rounded-2xl border-slate-100 p-10 text-center bg-gradient-to-br from-white to-brand-soft/30"
                data-testid="portal-investments-empty">
                <div className="mx-auto h-14 w-14 rounded-2xl bg-brand-soft grid place-items-center">
                    <TrendingUp className="h-6 w-6 text-brand-royal" strokeWidth={1.6} />
                </div>
                <h3 className="mt-4 font-display font-bold text-brand-navy text-xl">
                    No approved investments yet
                </h3>
                <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">
                    When you have an approved investment, it will appear here with maturity details and
                    transaction history.
                </p>
            </Card>
        );
    }

    const inv = items.find((i) => i.id === selectedId) || items[0];

    /* Label + value shown for the headline maturity number, per business spec:
       Active -> "Estimated Maturity Amount" (12% projection)
       Matured / Matured/Closed -> "Maturity Payout" (12% earned)
       Prematurely Closed -> "Final Payout" (principal-only refund, 0% interest)
    */
    const isPremature = inv.status === "Prematurely Closed";
    const isMaturedLike = inv.status === "Matured" || inv.status === "Matured/Closed";
    const payoutLabel = isPremature
        ? "Final Payout"
        : isMaturedLike
        ? "Maturity Payout"
        : "Estimated Maturity Amount";
    const payoutValue = isPremature
        ? Number(inv.final_payout_amount || 0)
        : Number(inv.estimated_maturity_amount || 0);

    const statusBadge = (s) => {
        const map = {
            Active:               "bg-emerald-50 text-emerald-700 border-emerald-100",
            Matured:              "bg-blue-50 text-blue-800 border-blue-100",
            "Matured/Closed":     "bg-blue-50 text-blue-800 border-blue-100",
            "Prematurely Closed": "bg-slate-100 text-slate-700 border-slate-200",
        };
        return (
            <Badge className={`${map[s] || "bg-slate-100 text-slate-700"} hover:opacity-100`}
                data-testid="portal-investment-status"
            >
                {s}
            </Badge>
        );
    };

    const typeBadge = (t) => {
        const map = {
            Deposit:  "bg-emerald-50 text-emerald-700 border-emerald-100",
            Payout:   "bg-brand-soft text-brand-royal border-brand-soft",
        };
        return (
            <Badge className={`${map[t] || "bg-slate-100 text-slate-700"} hover:opacity-100`}>
                {t}
            </Badge>
        );
    };

    return (
        <div className="space-y-5" data-testid="portal-investments">
            {/* Investment selector */}
            <div className="flex flex-wrap gap-2" data-testid="portal-investment-tabs">
                {items.map((i) => (
                    <button
                        key={i.id}
                        onClick={() => setSelectedId(i.id)}
                        className={`h-10 rounded-full px-4 text-xs font-semibold border transition ${
                            selectedId === i.id
                                ? "bg-brand-navy text-white border-brand-navy"
                                : "bg-white text-slate-700 border-slate-200 hover:border-brand-royal"
                        }`}
                        data-testid={`portal-investment-tab-${i.id}`}
                    >
                        <span className="font-mono">{i.account_code}</span>
                    </button>
                ))}
            </div>

            {/* Meta card */}
            <Card className="rounded-2xl border-slate-100 p-5 sm:p-6" data-testid="portal-investment-meta">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-display font-bold text-brand-navy text-lg">
                            Investment Account
                        </div>
                        <span className="text-[10px] font-mono uppercase bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                            {inv.account_code}
                        </span>
                        {statusBadge(inv.status)}
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                            {payoutLabel}
                        </div>
                        <div className="num text-2xl font-bold text-brand-royal"
                            data-testid="portal-investment-maturity"
                        >
                            {formatINR(payoutValue)}
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-5 pt-5 border-t border-slate-100">
                    <Stat label="Account Number" value={inv.account_code} />
                    <Stat label="Principal Amount" value={formatINR(inv.principal)} />
                    <Stat label="Start Date" value={formatDate(inv.start_date)} />
                    <Stat label="Maturity Date" value={inv.maturity_date ? formatDate(inv.maturity_date) : "—"} />
                    <Stat label="Tenure" value="365 days" />
                    <Stat label="Applicable Rate" value={`${inv.applicable_rate}% p.a.`} />
                    <Stat label={payoutLabel} value={formatINR(payoutValue)} accent />
                    <Stat label="Status" value={inv.status} />
                </div>

                <Card className="mt-5 rounded-xl border-brand-soft bg-brand-soft/40 p-4 flex items-start gap-3">
                    <div className="h-8 w-8 rounded-lg bg-white grid place-items-center shrink-0">
                        <Info className="h-4 w-4 text-brand-royal" strokeWidth={1.75} />
                    </div>
                    <div className="text-xs text-slate-700 leading-relaxed">
                        <div className="font-semibold">Product terms</div>
                        Before 365 days: principal-only premature payout, 0% interest. At 365 days:
                        12% simple interest and maturity. No partial withdrawal. No post-maturity interest.
                    </div>
                </Card>
            </Card>

            {/* Transactions — desktop table */}
            <Card className="rounded-2xl border-slate-100 overflow-hidden hidden md:block"
                data-testid="portal-investment-txns"
            >
                <div className="px-5 py-4 border-b border-slate-100">
                    <div className="text-[11px] uppercase tracking-widest text-brand-royal font-semibold">
                        Transaction History
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500">Approved transactions only.</div>
                </div>
                {inv.transactions.length === 0 ? (
                    <div className="p-10 text-center text-sm text-slate-400">
                        No approved transactions yet.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                                <tr>
                                    <th className="text-left px-4 py-3">Date</th>
                                    <th className="text-right px-4 py-3">Amount</th>
                                    <th className="text-center px-4 py-3">Type</th>
                                    <th className="text-left px-4 py-3">Reference No.</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {inv.transactions.map((tx) => (
                                    <tr key={tx.id} className="hover:bg-slate-50/70"
                                        data-testid={`portal-investment-tx-${tx.id}`}
                                    >
                                        <td className="px-4 py-3 font-medium text-slate-800">
                                            {tx.date ? formatDate(tx.date) : "—"}
                                        </td>
                                        <td className="px-4 py-3 num text-right text-slate-800">
                                            {formatINR(tx.amount)}
                                        </td>
                                        <td className="px-4 py-3 text-center">{typeBadge(tx.type)}</td>
                                        <td className="px-4 py-3 font-mono text-xs text-slate-500">
                                            {tx.reference}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {/* Transactions — mobile cards */}
            <div className="md:hidden space-y-3" data-testid="portal-investment-txns-mobile">
                {inv.transactions.length === 0 ? (
                    <Card className="rounded-2xl border-slate-100 p-8 text-center text-sm text-slate-400">
                        No approved transactions yet.
                    </Card>
                ) : inv.transactions.map((tx) => (
                    <Card key={tx.id} className="rounded-2xl border-slate-100 p-4"
                        data-testid={`portal-investment-mtx-${tx.id}`}
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                                    Date
                                </div>
                                <div className="mt-0.5 font-semibold text-slate-900">
                                    {tx.date ? formatDate(tx.date) : "—"}
                                </div>
                            </div>
                            {typeBadge(tx.type)}
                        </div>
                        <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-slate-100">
                            <Stat label="Amount" value={formatINR(tx.amount)} />
                            <Stat label="Reference" value={<span className="font-mono text-xs">{tx.reference}</span>} />
                        </div>
                    </Card>
                ))}
            </div>
        </div>
    );
}

/* ---------- Saving Scheme ---------- */
function deriveSavingsDisplay(meta, entries) {
    if (!meta || !meta.start_date) return null;
    const start = new Date(meta.start_date + "T00:00:00Z");
    const now = new Date();
    const daysElapsed = Math.floor((now - start) / 86400000);
    const backendStatus = String(meta.status || "").toLowerCase();
    // Prematurely closed / cancelled: honour the backend status verbatim.
    const closedLike = ["closed", "cancelled", "canceled", "premature_closed", "prematurely_closed"];
    let displayStatus;
    if (closedLike.includes(backendStatus)) {
        displayStatus = backendStatus.replace(/_/g, " ");
    } else if (daysElapsed >= 365) {
        displayStatus = "Matured";
    } else {
        const hasLate = Array.isArray(entries) && entries.some((e) => e.type === "Late");
        displayStatus = hasLate ? "Irregular" : "Active";
    }
    // Rate follows days elapsed (tiers align with the public Saving Scheme page).
    const applicableRate = daysElapsed >= 365 ? 5.0 : daysElapsed >= 180 ? 3.0 : 0.0;
    // Matured account: no next posting date.
    const showNextPosting = displayStatus !== "Matured";
    return { displayStatus, applicableRate, showNextPosting };
}

function DailySavings({ accounts, token }) {
    const [selectedId, setSelectedId] = useState(accounts[0]?.id || "");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (!selectedId || !token) return;
        setLoading(true);
        setErr("");
        setData(null);
        axios
            .get(`${API_BASE}/portal/savings/entries/${selectedId}`, { params: { t: token } })
            .then((r) => {
                const d = r.data;
                // Customer-facing rename: PG#### → SS####
                if (d?.meta?.account_code) {
                    d.meta.account_code = String(d.meta.account_code).replace(/^PG/, "SS");
                }
                setData(d);
            })
            .catch((e) => setErr(e?.response?.data?.detail || "Could not load Saving Scheme"))
            .finally(() => setLoading(false));
    }, [selectedId, token]);

    if (accounts.length === 0) {
        return (
            <Card className="rounded-2xl border-slate-100 p-10 text-center text-sm text-slate-400"
                data-testid="portal-savings"
            >
                No Saving Scheme accounts yet.
            </Card>
        );
    }

    const typeBadge = (type) => {
        const map = {
            Scheduled:  "bg-emerald-50 text-emerald-700 border-emerald-100",
            Late:       "bg-amber-50 text-amber-800 border-amber-100",
            Advance:    "bg-brand-soft text-brand-royal border-brand-soft",
            Withdrawal: "bg-rose-50 text-rose-700 border-rose-100",
        };
        return (
            <Badge className={`${map[type] || "bg-slate-100 text-slate-700"} hover:opacity-100`}>
                {type}
            </Badge>
        );
    };

    const view = data && !loading && !err ? deriveSavingsDisplay(data.meta, data.entries) : null;

    return (
        <div className="space-y-5" data-testid="portal-savings">
            {/* Account selector */}
            <div className="flex flex-wrap gap-2" data-testid="portal-savings-tabs">
                {accounts.map((a) => (
                    <button
                        key={a.id}
                        onClick={() => setSelectedId(a.id)}
                        className={`h-10 rounded-full px-4 text-xs font-semibold border transition ${
                            selectedId === a.id
                                ? "bg-brand-navy text-white border-brand-navy"
                                : "bg-white text-slate-700 border-slate-200 hover:border-brand-royal"
                        }`}
                        data-testid={`portal-savings-tab-${a.id}`}
                    >
                        <span className="font-mono">{a.account_code}</span>
                        <span className="ml-1 capitalize text-slate-400">· {a.frequency}</span>
                    </button>
                ))}
            </div>

            {loading && (
                <Card className="rounded-2xl border-slate-100 p-10 text-center text-sm text-slate-400">
                    Loading Saving Scheme…
                </Card>
            )}

            {err && !loading && (
                <Card className="rounded-2xl border-rose-100 bg-rose-50 p-6 text-sm text-rose-700"
                    data-testid="portal-savings-error">
                    {err}
                </Card>
            )}

            {data && !loading && !err && (
                <>
                    {/* Account meta */}
                    <Card className="rounded-2xl border-slate-100 p-5 sm:p-6" data-testid="portal-savings-meta">
                        <div className="flex items-start justify-between flex-wrap gap-3">
                            <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <div className="font-display font-bold text-brand-navy text-lg">
                                        Saving Scheme Account
                                    </div>
                                    <span className="text-[10px] font-mono uppercase bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                        {data.meta.account_code}
                                    </span>
                                    <Badge className="bg-brand-soft text-brand-royal border-brand-soft hover:bg-brand-soft capitalize">
                                        {data.meta.frequency}
                                    </Badge>
                                    <Badge className={view?.displayStatus === "Matured"
                                        ? "bg-blue-50 text-blue-800 border-blue-100 hover:bg-blue-50"
                                        : view?.displayStatus === "Irregular"
                                        ? "bg-amber-50 text-amber-800 border-amber-100 hover:bg-amber-50"
                                        : view?.displayStatus === "Active"
                                        ? "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50"
                                        : "bg-slate-100 text-slate-700 hover:bg-slate-100"}
                                        data-testid="portal-savings-status"
                                    >
                                        {view?.displayStatus || data.meta.status}
                                    </Badge>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                                    Current Balance
                                </div>
                                <div className="num text-2xl font-bold text-emerald-700"
                                    data-testid="portal-savings-balance"
                                >
                                    {formatINR(data.meta.current_balance)}
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-5 pt-5 border-t border-slate-100">
                            <Stat label="Account Number" value={data.meta.account_code} />
                            <Stat label="Frequency" value={data.meta.frequency[0].toUpperCase() + data.meta.frequency.slice(1)} />
                            <Stat label="Contribution Amount" value={formatINR(data.meta.installment_amount)} />
                            <Stat label="Start Date" value={formatDate(data.meta.start_date)} />
                            <Stat label="Approved Deposits" value={formatINR(data.meta.total_deposited)} />
                            <Stat label="Current Balance" value={formatINR(data.meta.current_balance)} accent />
                            <Stat label="Applicable Rate" value={`${view ? view.applicableRate : data.meta.applicable_rate}% p.a.`} />
                            <Stat label="Next Posting Date"
                                value={view && !view.showNextPosting
                                    ? "—"
                                    : (data.meta.next_posting_date ? formatDate(data.meta.next_posting_date) : "—")} />
                        </div>
                    </Card>

                    {/* Withdrawal card */}
                    <WithdrawSection
                        aid={selectedId}
                        token={token}
                        meta={data.meta}
                        onSubmitted={() => {
                            /* refresh entries + meta so the pending banner appears */
                            axios
                                .get(`${API_BASE}/portal/savings/entries/${selectedId}`, { params: { t: token } })
                                .then((r) => setData(r.data))
                                .catch(() => { /* keep current view */ });
                        }}
                    />

                    {/* Passbook — desktop table */}
                    <Card className="rounded-2xl border-slate-100 overflow-hidden hidden md:block"
                        data-testid="portal-savings-passbook"
                    >
                        <div className="px-5 py-4 border-b border-slate-100">
                            <div className="text-[11px] uppercase tracking-widest text-brand-royal font-semibold">
                                Passbook
                            </div>
                            <div className="mt-0.5 text-xs text-slate-500">Approved postings only.</div>
                        </div>
                        {data.entries.length === 0 ? (
                            <div className="p-10 text-center text-sm text-slate-400">
                                No approved deposits yet.
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                                        <tr>
                                            <th className="text-left px-4 py-3">Posting Date</th>
                                            <th className="text-right px-4 py-3">Amount</th>
                                            <th className="text-center px-4 py-3">Type</th>
                                            <th className="text-right px-4 py-3">Running Balance</th>
                                            <th className="text-left px-4 py-3">Reference No.</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {data.entries.map((e) => (
                                            <tr key={e.id} className="hover:bg-slate-50/70"
                                                data-testid={`portal-savings-entry-${e.id}`}
                                            >
                                                <td className="px-4 py-3 font-medium text-slate-800">
                                                    {e.posting_date ? formatDate(e.posting_date) : "—"}
                                                </td>
                                                <td className="px-4 py-3 num text-right text-slate-800">
                                                    <span className={Number(e.amount) < 0 ? "text-rose-700 font-semibold" : ""}>
                                                        {Number(e.amount) < 0
                                                            ? `- ${formatINR(Math.abs(Number(e.amount)))}`
                                                            : formatINR(e.amount)}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-center">{typeBadge(e.type)}</td>
                                                <td className="px-4 py-3 num text-right text-emerald-700 font-semibold">
                                                    {formatINR(e.running_balance)}
                                                </td>
                                                <td className="px-4 py-3 font-mono text-xs text-slate-500">
                                                    {e.reference}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Card>

                    {/* Passbook — mobile cards */}
                    <div className="md:hidden space-y-3" data-testid="portal-savings-passbook-mobile">
                        {data.entries.length === 0 ? (
                            <Card className="rounded-2xl border-slate-100 p-8 text-center text-sm text-slate-400">
                                No approved deposits yet.
                            </Card>
                        ) : data.entries.map((e) => (
                            <Card key={e.id} className="rounded-2xl border-slate-100 p-4"
                                data-testid={`portal-savings-mentry-${e.id}`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                                            Posting Date
                                        </div>
                                        <div className="mt-0.5 font-semibold text-slate-900">
                                            {e.posting_date ? formatDate(e.posting_date) : "—"}
                                        </div>
                                    </div>
                                    {typeBadge(e.type)}
                                </div>
                                <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-slate-100">
                                    <Stat label="Amount" value={formatINR(e.amount)} />
                                    <Stat label="Running Balance" value={formatINR(e.running_balance)} accent />
                                </div>
                                <div className="mt-2 text-[11px] font-mono text-slate-400">
                                    Ref: {e.reference}
                                </div>
                            </Card>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

/* ---------- Withdraw Section (Saving Scheme) ---------- */
function WithdrawSection({ aid, token, meta, onSubmitted }) {
    const balance = Number(meta?.current_balance || 0);
    const [amount, setAmount] = useState("");
    const [preview, setPreview] = useState(null);
    const [prevLoading, setPrevLoading] = useState(false);
    const [prevErr, setPrevErr] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [requests, setRequests] = useState([]);

    /* Load this account's active + historical requests (customer-scoped subset via /portal/me is
       not currently available, so we treat pending/approved requests as ephemeral UI feedback
       returned by submit. We refresh only after a submit + on mount from the returned doc). */
    useEffect(() => {
        setAmount("");
        setPreview(null);
        setRequests([]);
        setPrevErr("");
    }, [aid]);

    /* Debounced preview fetch as user types */
    useEffect(() => {
        const num = Number(amount);
        if (!aid || !token || !num || num <= 0) {
            setPreview(null);
            setPrevErr("");
            return;
        }
        if (num > balance) {
            setPreview(null);
            setPrevErr(`Amount cannot exceed current balance of ${formatINR(balance)}.`);
            return;
        }
        setPrevErr("");
        setPrevLoading(true);
        const h = setTimeout(() => {
            axios
                .get(`${API_BASE}/portal/savings/withdraw-preview/${aid}`, {
                    params: { t: token, amount: num },
                })
                .then((r) => setPreview(r.data))
                .catch((e) => {
                    setPreview(null);
                    setPrevErr(e?.response?.data?.detail || "Could not preview withdrawal");
                })
                .finally(() => setPrevLoading(false));
        }, 350);
        return () => clearTimeout(h);
    }, [amount, aid, token, balance]);

    async function submit() {
        const num = Number(amount);
        if (!num || num <= 0) {
            toast.error("Enter an amount to withdraw");
            return;
        }
        if (num > balance) {
            toast.error("Amount exceeds current balance");
            return;
        }
        try {
            setSubmitting(true);
            const { data } = await axios.post(
                `${API_BASE}/portal/savings/withdraw-request`,
                { t: token, amount: num },
                { params: { aid } },
            );
            setRequests((r) => [data, ...r]);
            setAmount("");
            setPreview(null);
            toast.success("Withdrawal request submitted for approval");
            onSubmitted?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not submit request");
        } finally {
            setSubmitting(false);
        }
    }

    const tierCopy = (t) => t === "<180"
        ? "Withdrawn before 180 days — 3% penalty on principal, 0% interest."
        : t === "180-364"
        ? "Withdrawn between 180–364 days — 3% p.a. simple interest, no penalty."
        : t === "365+"
        ? "Withdrawn after 365 days — 5% p.a. simple interest, no penalty."
        : "";

    return (
        <Card className="rounded-2xl border-slate-100 p-5 sm:p-6" data-testid="portal-withdraw-card">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-brand-royal font-semibold">
                        Withdraw
                    </div>
                    <h3 className="mt-1 font-display font-bold text-brand-navy text-lg">
                        Request a payout
                    </h3>
                    <p className="mt-1 text-xs text-slate-500 max-w-md">
                        Preview your net payout before submitting. Balance is not deducted until the
                        request is approved and paid by our team.
                    </p>
                </div>
                <div className="h-10 w-10 rounded-xl bg-brand-soft grid place-items-center">
                    <ArrowDownCircle className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                </div>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
                {/* Amount input */}
                <div>
                    <Label htmlFor="wd-amount" className="text-xs font-semibold text-slate-600">
                        Amount to withdraw (₹)
                    </Label>
                    <Input
                        id="wd-amount"
                        type="number"
                        inputMode="numeric"
                        min={0}
                        step={1}
                        placeholder={`Up to ${formatINR(balance)}`}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="mt-1.5 h-11 rounded-xl num"
                        data-testid="portal-withdraw-amount"
                    />
                    <div className="mt-2 flex flex-wrap gap-1.5">
                        {[500, 1000, 2500, 5000].filter((v) => v <= balance).map((v) => (
                            <button
                                key={v}
                                type="button"
                                onClick={() => setAmount(String(v))}
                                className="text-[11px] font-semibold rounded-full px-2.5 py-1 border border-brand-soft text-brand-royal hover:bg-brand-soft/60"
                                data-testid={`portal-withdraw-quick-${v}`}
                            >
                                {formatINR(v)}
                            </button>
                        ))}
                        {balance > 0 && (
                            <button
                                type="button"
                                onClick={() => setAmount(String(Math.floor(balance)))}
                                className="text-[11px] font-semibold rounded-full px-2.5 py-1 border border-brand-soft text-brand-royal hover:bg-brand-soft/60"
                                data-testid="portal-withdraw-quick-max"
                            >
                                Max
                            </button>
                        )}
                    </div>
                    {prevErr && (
                        <div className="mt-2 text-xs text-rose-600" data-testid="portal-withdraw-error">
                            {prevErr}
                        </div>
                    )}
                </div>

                {/* Preview panel */}
                <div>
                    <div className="text-xs font-semibold text-slate-600">Preview</div>
                    {(!amount || Number(amount) <= 0) ? (
                        <div className="mt-1.5 rounded-xl border border-dashed border-slate-200 p-4 text-xs text-slate-400 text-center"
                            data-testid="portal-withdraw-preview-empty">
                            Enter an amount above to see the payout breakdown.
                        </div>
                    ) : prevLoading ? (
                        <div className="mt-1.5 rounded-xl border border-slate-100 p-4 text-xs text-slate-500 text-center">
                            Calculating…
                        </div>
                    ) : preview ? (
                        <div className="mt-1.5 rounded-xl border border-slate-100 p-4"
                            data-testid="portal-withdraw-preview">
                            <div className="grid grid-cols-2 gap-3">
                                <Stat label="Tier" value={preview.tier} />
                                <Stat label="Days Elapsed" value={`${preview.days_elapsed}d`} />
                                <Stat label="Principal" value={formatINR(preview.principal)} />
                                <Stat label={`Interest (${preview.applicable_rate}% p.a.)`}
                                    value={formatINR(preview.interest)} />
                                <Stat label="Penalty" value={formatINR(preview.penalty)} />
                                <Stat label="Remaining Balance"
                                    value={formatINR(preview.remaining_balance)} />
                            </div>
                            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                                    Estimated Net Payout
                                </div>
                                <div className="num text-lg font-bold text-emerald-700"
                                    data-testid="portal-withdraw-net">
                                    {formatINR(preview.net_payout)}
                                </div>
                            </div>
                            <div className="mt-2 text-[11px] text-slate-500 italic">
                                {tierCopy(preview.tier)}
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-slate-100">
                <div className="text-[11px] text-slate-500">
                    Withdrawal requests are reviewed by our team. You will be notified once paid.
                </div>
                <Button
                    onClick={submit}
                    disabled={submitting || !preview || !!prevErr}
                    className="h-11 rounded-xl bg-brand-navy hover:bg-brand-royal"
                    data-testid="portal-withdraw-submit"
                >
                    {submitting ? "Submitting…" : "Submit withdrawal request"}
                </Button>
            </div>

            {/* Recently submitted (ephemeral) */}
            {requests.length > 0 && (
                <div className="mt-5 space-y-2" data-testid="portal-withdraw-recent">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                        Recently submitted
                    </div>
                    {requests.map((r) => (
                        <div key={r.id}
                            className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 p-3"
                            data-testid={`portal-withdraw-req-${r.id}`}
                        >
                            <div>
                                <div className="flex items-center gap-2 text-xs">
                                    <Clock className="h-3.5 w-3.5 text-amber-600" />
                                    <span className="font-semibold text-slate-800 capitalize">
                                        {r.status}
                                    </span>
                                    <span className="text-slate-500">
                                        · {formatINR(r.principal)} · net {formatINR(r.net_payout)}
                                    </span>
                                </div>
                                <div className="text-[11px] text-slate-500 mt-0.5">
                                    {formatDate(r.request_date)}
                                </div>
                            </div>
                            <Badge className="bg-amber-50 text-amber-800 border-amber-100 hover:bg-amber-50">
                                Awaiting approval
                            </Badge>
                        </div>
                    ))}
                </div>
            )}
        </Card>
    );
}

/* ---------- Repayment Schedule ---------- */
function RepaymentSchedule({ loans, token }) {
    const [selectedId, setSelectedId] = useState(loans[0]?.id || "");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (!selectedId || !token) return;
        setLoading(true);
        setErr("");
        setData(null);
        axios
            .get(`${API_BASE}/portal/schedule/${selectedId}`, { params: { t: token } })
            .then((r) => setData(r.data))
            .catch((e) => setErr(e?.response?.data?.detail || "Could not load schedule"))
            .finally(() => setLoading(false));
    }, [selectedId, token]);

    if (loans.length === 0) {
        return (
            <Card className="rounded-2xl border-slate-100 p-10 text-center text-sm text-slate-400"
                data-testid="portal-schedule">
                No approved loans to schedule.
            </Card>
        );
    }

    const statusBadge = (s) => {
        const map = {
            paid:      "bg-emerald-50 text-emerald-700 border-emerald-100",
            partial:   "bg-amber-50 text-amber-800 border-amber-100",
            upcoming:  "bg-slate-100 text-slate-700",
            due_soon:  "bg-amber-50 text-amber-800 border-amber-100",
            past_due:  "bg-orange-50 text-orange-700 border-orange-100",
            overdue:   "bg-rose-50 text-rose-700 border-rose-100",
        };
        const labels = {
            paid: "Paid",
            partial: "Partial",
            upcoming: "Upcoming",
            due_soon: "Due Soon",
            past_due: "Past Due",
            overdue: "Overdue",
        };
        return (
            <Badge className={`${map[s] || "bg-slate-100 text-slate-700"} hover:opacity-100`}>
                {labels[s] || s}
            </Badge>
        );
    };

    return (
        <div className="space-y-5" data-testid="portal-schedule">
            {/* Loan selector */}
            <div className="flex flex-wrap gap-2" data-testid="portal-schedule-tabs">
                {loans.map((ln) => (
                    <button
                        key={ln.id}
                        onClick={() => setSelectedId(ln.id)}
                        className={`h-10 rounded-full px-4 text-xs font-semibold border transition ${
                            selectedId === ln.id
                                ? "bg-brand-navy text-white border-brand-navy"
                                : "bg-white text-slate-700 border-slate-200 hover:border-brand-royal"
                        }`}
                        data-testid={`portal-schedule-tab-${ln.id}`}
                    >
                        <span className="capitalize">{ln.loan_type}</span>{" "}
                        <span className="font-mono">· {ln.loan_code}</span>
                    </button>
                ))}
            </div>

            {loading && (
                <Card className="rounded-2xl border-slate-100 p-10 text-center text-sm text-slate-400">
                    Loading schedule…
                </Card>
            )}

            {err && !loading && (
                <Card className="rounded-2xl border-rose-100 bg-rose-50 p-6 text-sm text-rose-700"
                    data-testid="portal-schedule-error">
                    {err}
                </Card>
            )}

            {data && !loading && !err && (
                <>
                    {/* Meta card */}
                    <Card className="rounded-2xl border-slate-100 p-5 sm:p-6" data-testid="portal-schedule-meta">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            <Stat label="Loan A/C No." value={data.meta.loan_code} />
                            <Stat label="Frequency" value="Monthly" />
                            <Stat label="Interest Rate" value={`${data.meta.interest_rate}% / month`} />
                            <Stat label="Method"
                                value={data.meta.payment_mode === "interest_only" ? "Interest-only" : "Monthly EMI"} />
                            <Stat label="Tenure" value={`${data.meta.tenure_months} months`} />
                            <Stat label="Start" value={formatDate(data.meta.start_date)} />
                            <Stat label="Next Due Date"
                                value={data.meta.next_due_date ? formatDate(data.meta.next_due_date) : "—"} accent />
                            <Stat label="Next Due Amount"
                                value={formatINR(data.meta.next_due_amount)} accent />
                        </div>
                    </Card>

                    {/* Desktop table */}
                    <Card className="rounded-2xl border-slate-100 overflow-hidden hidden md:block"
                        data-testid="portal-schedule-table"
                    >
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                                    <tr>
                                        <th className="text-left px-4 py-3">#</th>
                                        <th className="text-left px-4 py-3">Due Date</th>
                                        <th className="text-right px-4 py-3">Scheduled</th>
                                        <th className="text-right px-4 py-3">Interest</th>
                                        <th className="text-right px-4 py-3">Principal</th>
                                        <th className="text-right px-4 py-3">Approved Paid</th>
                                        <th className="text-right px-4 py-3">Balance</th>
                                        <th className="text-center px-4 py-3">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {data.rows.map((r) => (
                                        <tr key={r.cycle} className="hover:bg-slate-50/70"
                                            data-testid={`portal-schedule-row-${r.cycle}`}
                                        >
                                            <td className="px-4 py-3 text-slate-500 font-mono">{r.cycle}</td>
                                            <td className="px-4 py-3 font-medium text-slate-800">{formatDate(r.due_date)}</td>
                                            <td className="px-4 py-3 num text-right text-slate-800">{formatINR(r.scheduled_amount)}</td>
                                            <td className="px-4 py-3 num text-right text-slate-600">{formatINR(r.interest)}</td>
                                            <td className="px-4 py-3 num text-right text-slate-600">{formatINR(r.principal)}</td>
                                            <td className="px-4 py-3 num text-right text-emerald-700 font-semibold">{formatINR(r.approved_paid)}</td>
                                            <td className="px-4 py-3 num text-right text-slate-800">{formatINR(r.remaining_balance)}</td>
                                            <td className="px-4 py-3 text-center">{statusBadge(r.status)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>

                    {/* Mobile stacked cards */}
                    <div className="md:hidden space-y-3" data-testid="portal-schedule-mobile">
                        {data.rows.map((r) => (
                            <Card key={r.cycle} className="rounded-2xl border-slate-100 p-4"
                                data-testid={`portal-schedule-mrow-${r.cycle}`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                                            Cycle {r.cycle}
                                        </div>
                                        <div className="mt-0.5 font-semibold text-slate-900">
                                            {formatDate(r.due_date)}
                                        </div>
                                    </div>
                                    {statusBadge(r.status)}
                                </div>
                                <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-slate-100">
                                    <Stat label="Scheduled" value={formatINR(r.scheduled_amount)} />
                                    <Stat label="Approved Paid" value={formatINR(r.approved_paid)} />
                                    <Stat label="Interest" value={formatINR(r.interest)} />
                                    <Stat label="Principal" value={formatINR(r.principal)} />
                                    <Stat label="Balance" value={formatINR(r.remaining_balance)} />
                                </div>
                            </Card>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
